import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import moment from "moment";

import {
  Search,
  Map as MapIcon,
  Grid,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Monitor,
  Users,
  ArrowLeft,
  List,
  X,
  Info,
  Clock
} from "lucide-react";
import PackageCard from "@/components/package-card";
import DealCard from "@/components/DealCard";
import MapView from "@/components/map-view";
import { getMediaOwner } from "@/data/media-owners";
import { useNavigate, Link } from "react-router-dom";

import debounce from 'lodash/debounce';
import type { BillboardFilters, CartItem } from '../types/billboard';
import { generateMockData } from '../Graphql/MockData';
import { GETPROPERTY_LIST } from '../Graphql/PropertyListQueries';
// import { toast } from 'react-toastify';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ADD_TO_CART_SUBMIT, DELETE_CART_SUBMIT, GET_MY_CART_ITEMS } from '../Graphql/cartQueries';
import { PACKAGE_BILLBOARD } from '../Graphql/packageQurie';
import { parse, isValid, format } from 'date-fns';
import client, { MAPBOX_TOKEN } from '../Graphql/apolloClient';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CART_PAGE_ITEM, GET_DISTRICT_LIST_MARKETPLACE, GET_STATE_LIST_MARKETPLACE, GET_MARKETPLACE_COUNTRIES, INVOICE, COMPANY_LIST, PROPERTY_LIST_VENUE_TYPE_LIST, GET_STATE_LIST } from '../Graphql/Queries';
import { useCart } from '@/utils/cartContext';
import Header from '@/components/header';
import Footer from '@/components/Footer1';
import { useToast } from '@/hooks/use-toast';

// Default filter values
const defaultFilters = {
  country: '', // Will be set from localStorage or country selection
  state: [],
  district: [],
  available: true,
  category: "digital",
  priceRange: null as [number, number] | null, // Changed to null initially
  venueType: "" as string,
  environment: "" as string,
  targetAgeRange: [] as string[],
  audienceType: [] as string[],
  targetGender: [] as string[],
  isIndoor: null as boolean | null,
  inventoryType: [] as string[],
  adPlayMode: [] as string[],
  screenSize: [] as string[],
  billboardFormat: [] as string[],
  orientation: [] as string[],
  mediaOwner: [] as string[]
};

const defaultExpandedSections = {
  location: true,
  category: true,
  buyingFormat: true,
  pricing: false,
  valuePacks: false,
  environment: false,
  mediaOwner: false,
  venue: false,
  audience: false,
  inventorySpec: false
};

const formatNumber = (num: number | undefined | null): string => {
  if (!num || typeof num !== 'number') return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toString();
  }
};


// Safe date formatter that handles null values
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = ''): string => {
  if (!date) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return fallback;
  }
};

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN;

// ✅ Memoized FilterSection component (moved outside to prevent recreation on every render)
const FilterSection = React.memo(({ title, isExpanded, onToggle, children, actionButton }: any) => (
  <div className="border-b border-gray-200 pb-4 mb-4">
    <div className="flex items-center justify-between w-full gap-2">
      <button
        onClick={onToggle}
        className="flex items-center flex-1 text-left"
      >
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      </button>
      <div className="flex items-center gap-1">
        {actionButton}
        <button
          onClick={onToggle}
          className="flex items-center justify-center"
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>
      </div>
    </div>
    {isExpanded && <div className="mt-3 space-y-3">{children}</div>}
  </div>
));

FilterSection.displayName = 'FilterSection';

// ✅ Memoized CheckboxGroup component (moved outside to prevent recreation on every render)
const CheckboxGroup = React.memo(({ options, value, onChange }: any) => (
  <div className="space-y-2">
    {options.map((option: string) => (
      <div key={option} className="flex items-center space-x-2">
        <Checkbox
          id={option}
          checked={value.includes(option)}
          onCheckedChange={() => onChange(option)}
        />
        <Label htmlFor={option} className="text-sm text-gray-700 cursor-pointer">
          {option}
        </Label>
      </div>
    ))}
  </div>
));

CheckboxGroup.displayName = 'CheckboxGroup';

type Field = {
  key: string;
  type: string;
  label: string;
  default: boolean | string;
  disabled?: boolean;
  options?: { label: string; value: string }[];
};

type Configuration = {
  key: string;
  label: string;
  fields?: Field[];
  options?: { label: string; value: string }[];
  type?: string;
  default?: boolean | string;
  disabled?: boolean;
};

type ConfigPage = {
  name: string;
  configurations: Configuration[];
};

interface CampaignInventory {
  id: string;
  cartItemId: string;
  [key: string]: any;
}



interface CheckoutProps {
  yourLogo?: string;
  footerLogo?: string;
  base_path?: string;
  currencyCode?: string;
  configData?: ConfigPage[];
  exposure?: string;
}

export default function StartCampaign(props?: CheckoutProps) {
  // Spot Based Booking flag for API payload - Restore from URL/localStorage
  const [spotBasedBooking, setSpotBasedBooking] = useState<boolean>(() => {
    // First check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlSpotBased = urlParams.get('spotBased');
    if (urlSpotBased !== null) {
      const value = urlSpotBased === 'true';
      return value;
    }
    // Then check localStorage
    const savedSpotBased = localStorage.getItem('proSpotBasedBooking');
    if (savedSpotBased !== null) {
      const value = savedSpotBased === 'true';
      return value;
    }
    // Default to false (day-based)
    return false;
  });

  // Buying Format state: 'spot-based' or 'day-based' - Restore from URL/localStorage
  const [buyingFormat, setBuyingFormat] = useState<'spot-based' | 'day-based'>(() => {
    // First check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlFormat = urlParams.get('buyingFormat');
    if (urlFormat === 'spot-based' || urlFormat === 'day-based') {
      return urlFormat;
    }
    // Then check localStorage
    const savedFormat = localStorage.getItem('proBuyingFormat');
    if (savedFormat === 'spot-based' || savedFormat === 'day-based') {
      return savedFormat;
    }
    // Default to day-based
    return 'day-based';
  });

  // Track last countryId used for API call to prevent duplicate calls
  const lastCountryIdRef = useRef<string>();

  const cartInitializationRef = useRef(false);
  const initialCartLoadDone = useRef(false);
  const isManualBillboardTypeChange = useRef(false); // Track manual changes to prevent duplicate calls
  const isManualStateChange = useRef(false); // Track manual state changes to prevent duplicate calls
  const isManualDistrictChange = useRef(false); // Track manual district changes to prevent duplicate calls
  const isManualCountryChange = useRef(false); // Track manual country changes to prevent duplicate calls
  const currentEnvironmentRef = useRef<string>(''); // Add this line
  const currentVenueTypeRef = useRef<string>(''); // Track manual venue type changes to prevent duplicate calls
  const currentStateRef = useRef<string[]>([]); // Track current state selection
  const currentDistrictRef = useRef<string[]>([]); // Track current district selection

  // ➕ ADD THESE DEBUG REFS
  const nonDraftFetchCount = useRef(0);
  const mediaOwnerFetchCount = useRef(0);
  const packageFetchCount = useRef(0);
  const draftFetchCount = useRef(0);

  // Removed isManualPriceChange as it was causing issues with subsequent price filter calls

  // Deduplication refs to prevent multiple API calls for the same parameters
  const lastBillboardFetchRef = useRef<{ countryId?: string; page?: number; type?: string; timestamp?: number }>({});
  const lastPackageFetchRef = useRef<{ countryId?: string; page?: number; timestamp?: number }>({});

  const isFilterInitializedRef = useRef(false);

  // Country initialization tracking
  const countryInitialized = useRef(false);

  // Cart restoration lock to prevent duplicate API calls
  const cartRestorationInProgress = useRef(false);

  // Single source of truth for countryId using 2-variable priority system
  // Priority: Cart Country > selectedCountryId > sessionDefaultCountryId
  const [selectedCountryId, setSelectedCountryId] = useState<string>(() => {
    // Priority 1: URL parameter (navigation from detail page)
    const urlParams = new URLSearchParams(window.location.search);
    const urlCountryId = urlParams.get('countryId');
    if (urlCountryId) {
      localStorage.setItem('selectedCountryId', urlCountryId);
      return urlCountryId;
    }

    // Priority 2: localStorage (user's manual selection)
    const storedSelectedCountryId = localStorage.getItem('selectedCountryId');
    if (storedSelectedCountryId) {
      return storedSelectedCountryId;
    }

    // Priority 3: Session default
    const sessionDefault = localStorage.getItem('sessionDefaultCountryId');
    if (sessionDefault) {
      return sessionDefault;
    }

    return '';
  });
  const { toast } = useToast();
  const defaultProps: CheckoutProps = {
    base_path: '',
    currencyCode: '',
    configData: [],
    yourLogo: '',
    footerLogo: '',
    exposure: ''
  };
  const { base_path, currencyCode, configData, yourLogo, footerLogo, exposure } = { ...defaultProps, ...props };

  // Removed excessive render logging for performance
  // Base paths and currency code configured

  const navigate = useNavigate();
  const { isInCart, addToCart: addToCartContext, removeFromCart: removeFromCartContext, setCartItemCount: setCartItemCountContext, updateTaxInfo, cartItems: contextCartItems, setPriceFormat, priceFormat, setCartCountry, cartCountry, cartCountryId, hasCartCountry, updateStartDate: updateCartStartDate, updateEndDate: updateCartEndDate } = useCart();

  useEffect(() => {
    const today = new Date();
    updateCartStartDate(today);
    updateCartEndDate(today);
  }, []);

  // Add a function to get the default view mode from config
  const getDefaultViewMode = (): "grid" | "list" | "map" => {
    if (!configData || configData.length === 0) {
      return "grid";
    }

    const searchPageConfig = configData.find((config: ConfigPage) => config.name === "Search page");

    if (!searchPageConfig?.configurations) {
      return "grid";
    }

    const mapAndListViewConfig = searchPageConfig.configurations.find((config: Configuration) => config.key === "MapAndListView");

    if (!mapAndListViewConfig?.default) {
      return "grid";
    }

    // Directly return the default value as the view mode (now "grid", "list", or "map")
    const defaultView = mapAndListViewConfig.default as "grid" | "list" | "map";
    return ["grid", "list", "map"].includes(defaultView) ? defaultView : "grid";
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list" | "map">("grid"); // Start with grid as default
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([0, 0]);

  // Update viewMode when configData becomes available
  useEffect(() => {

    if (configData && configData.length > 0) {
      const defaultViewMode = getDefaultViewMode();
      setViewMode(defaultViewMode);
    }
  }, [configData]); // Re-run when configData changes
  const [minPriceInput, setMinPriceInput] = useState<string>('');
  const [maxPriceInput, setMaxPriceInput] = useState<string>('');

  // Initialize filters from localStorage or defaults
  const [expandedSections, setExpandedSections] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('start-campaign-expanded-sections');
      return saved ? JSON.parse(saved) : defaultExpandedSections;
    }
    return defaultExpandedSections;
  });


  // Initialize filters from localStorage or defaults
  const [filters, setFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('start-campaign-filters');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Ensure environment is properly initialized
          const initializedFilters = {
            ...defaultFilters,
            ...parsed,
            country: parsed.country || '',
            state: Array.isArray(parsed.state) ? parsed.state : parsed.state ? [parsed.state] : [],
            district: Array.isArray(parsed.district) ? parsed.district : parsed.district ? [parsed.district] : [],
            environment: parsed.environment || '', // Ensure empty string default
            venueType: parsed.venueType || '' // Ensure empty string default
          };

          // Update ref with initial environment value
          currentEnvironmentRef.current = initializedFilters.environment;
          currentVenueTypeRef.current = initializedFilters.venueType;
          currentStateRef.current = initializedFilters.state;
          currentDistrictRef.current = initializedFilters.district;

          return initializedFilters;
        } catch (e) {
          return {
            ...defaultFilters,
            country: '' // Will be set after countryOptions are loaded
          };
        }
      }
    }
    return {
      ...defaultFilters,
      country: '' // Will be set after countryOptions are loaded
    };
  });



  // Initialize campaign details with generic defaults
  const [startDate, setStartDate] = useState<Date>(() => {
    const firstDay = new Date();
    firstDay.setDate(1); // Go to first of current month
    return firstDay;
  }); // First day of current month
  const [endDate, setEndDate] = useState<Date>(() => {
    const lastDay = new Date();
    lastDay.setDate(1); // Go to first of current month
    lastDay.setMonth(lastDay.getMonth() + 1); // Go to first of next month
    lastDay.setDate(0); // Go to last day of current month
    return lastDay;
  });
  const [campaignName, setCampaignName] = useState<string>(() => {
    const date = new Date();
    return `Campaign_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${Math.random().toString(36).substring(7)}`;
  });



  const [filteredBillboards, setFilteredBillboards] = useState<any>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  // const [selectedCategory, setSelectedCategory] = useState<string>('Classic');
  const categoryRef = useRef<HTMLDivElement>(null);
  const [mapMode, setMapMode] = useState<'map' | 'satellite'>('map');
  const [cartDataList, setCartDataList] = useState<CampaignInventory[]>([]);
  // const [resultObj, setResultObj] = useState<any>([]);


  const parseDateWithFallback = (date: any, fallback: Date | null = null): Date | null => {
    if (!date || typeof date !== 'string') return fallback;

    const possibleFormats = ['yyyy-MM-dd', 'dd MMMM yyyy', 'MM/dd/yyyy'];
    for (const format of possibleFormats) {
      const parsedDate = parse(date, format, new Date());
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }

    const parsed = new Date(date);
    return isValid(parsed) ? parsed : fallback;
  };


  // Initialize state and district from local storage only
  const [state, setState] = useState<string[]>(() => {
    const savedListState = localStorage.getItem('campaignListState');
    if (savedListState) {
      try {
        const parsed = JSON.parse(savedListState);
        if (parsed.state) {
          if (Array.isArray(parsed.state)) return parsed.state.filter(Boolean);
          if (typeof parsed.state === 'string') return parsed.state.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
      } catch (e) {
        console.error('Error parsing campaignListState:', e);
      }
    }
    // Fallback: Try to get from individual localStorage keys
    const savedState = localStorage.getItem('campaignState');
    if (savedState) {
      return [savedState];
    }
    return [];
  });

  const [district, setDistrict] = useState<string[]>(() => {
    // Try to get from individual localStorage key
    const savedDistrict = localStorage.getItem('campaignDistrict');
    if (savedDistrict) {
      return [savedDistrict];
    }
    return [];
  });

  const [availableStates, setAvailableStates] = useState<any[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<any[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const [billBoardDataLoading, setBillBoardDataLoading] = useState(false);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [billBoardData, setBillBoardData] = useState<any>([]);
  // Package state management
  const [packageData, setPackageData] = useState<any>([]);
  const [packageDataLoading, setPackageDataLoading] = useState(false);
  const [packageTotalSize, setPackageTotalSize] = useState(0);
  // const [soldCampaignViewDetails, setSoldCampaignViewDetails] = useState<any>({});
  const [cartCount, setCartCount] = useState<number>(0);
  const [addtoCartQuery] = useMutation(ADD_TO_CART_SUBMIT);
  const [deleteCartQuery] = useMutation(DELETE_CART_SUBMIT);
  const [billboardType, setBillboardType] = useState<string>(() => {
    // First check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlBillboardType = urlParams.get('billboardType');
    if (urlBillboardType) {
      return urlBillboardType;
    }
    // Then check localStorage
    const savedType = localStorage.getItem('proBillboardType');
    if (savedType) {
      return savedType;
    }
    // Default to digital
    return 'digital';
  });

  // ✅ Sync filters.category with billboardType on mount and whenever billboardType changes
  useEffect(() => {
    setFilters((prev: typeof defaultFilters) => {
      if (prev.category !== billboardType) {
        return {
          ...prev,
          category: billboardType
        };
      }
      return prev;
    });
    localStorage.setItem('proBillboardType', billboardType);
  }, [billboardType]);

  // --- Dynamic Media Owner Filter ---
  // Fetch media owners dynamically using MEDIA_OWNER_FILTER
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : '';
  const countryName = typeof window !== 'undefined' ? localStorage.getItem('sessionDefaultCountryName') : '';
  const type = "MEDIA_OPERATOR";
  const authentication = true;

  // Replace the MEDIA_OWNER_FILTER query with COMPANY_LIST
  const { data: mediaOwnerData, loading: mediaOwnerLoading, error: mediaOwnerError, refetch: refetchMediaOwners } = useQuery(COMPANY_LIST, {
    variables: {
      exposure: exposure,
      countryId: selectedCountryId || localStorage.getItem('sessionDefaultCountryId'),
      page: 0,  // Assuming starting page is 0
      size: 100,  // Assuming a reasonable size for the list
    },
    skip: !selectedCountryId,
    fetchPolicy: 'cache-first',
    onError: (error) => {
      console.error('Media Owner Filter API Error:', error);
    }
  });

  // Refetch media owners when country changes
  useEffect(() => {
    if (selectedCountryId) {
      refetchMediaOwners({
        exposure: exposure,
        countryId: selectedCountryId,
        page: 0,
        size: 100,
      });
    }
  }, [selectedCountryId, exposure, refetchMediaOwners]);

  // Extract media owner options for filter dropdown with error handling
  const mediaOwnerOptions = (() => {
    if (mediaOwnerError) {
      console.error('Error loading media owners:', mediaOwnerError);
      return []; // Return empty array if there's an error
    }

    if (mediaOwnerLoading) {
      return []; // Return empty array while loading
    }

    if (!mediaOwnerData?.companyList) {
      console.warn('No media owner data available');
      return [];
    }

    // Sort media owners alphabetically by name before mapping
    return mediaOwnerData.companyList
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((owner: any) => ({
        label: owner.name,
        value: owner.id,
      }));
  })();

  // Use mediaOwnerOptions in your filter UI instead of hardcoded values

  const dataListLength = 12;
  const [totalDataLength, setTotalDataLength] = useState(0);
  const [locationData, setLocationData] = useState<string[]>([]);
  const [venueTypeData, setVenueTypeData] = useState<string[]>([]);
  const [siteTypeData, setSitetypeData] = useState<string[]>([]);
  const mapContainer = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  // const companyId = import.meta.env.VITE_APP_COMPANY_ID;

  // Additional state variables that are used in functions
  const [pageStartData, setPageStartData] = useState(0);
  const [pageEndData, setPageEndData] = useState(0);
  const [digitalTotalSize, setdigitalTotalSize] = useState(0);
  const [clasicTotalSize, setclasicTotalSize] = useState(0);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [cartData, setCartData] = useState<any>([{}]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);

  const [campaignId, setCampaignId] = useState<string | null>(() => {
    const id = urlParams.get('draftId');
    return id ? id : " ";
  });

  const [draftData, setDraftData] = useState<any>(null);
  const [draftInitialized, setDraftInitialized] = useState(false);
  const [campaignData, setCampaignData] = useState<any>(null);

  const [edit, setEdit] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('edit') === 'true';
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editFlag = urlParams.get('edit') === 'true';
    if (editFlag !== edit) {
      setEdit(editFlag);
    }
  }, []); // Remove dependencies to prevent loops, run only on mount
  const [source, setSource] = useState('');

  // Additional state variables that are used in functions
  const [badges, setBadges] = useState<string[]>([]);
  const [indoorChecked, setIndoorChecked] = useState(false);
  const [outdoorChecked, setOutdoorChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(() => {
    // First check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlPage = urlParams.get('page');
    if (urlPage && !isNaN(parseInt(urlPage))) {
      const page = parseInt(urlPage);
      return page;
    }
    // Then check localStorage
    const savedPage = localStorage.getItem('propcurrentPage');
    if (savedPage && !isNaN(parseInt(savedPage))) {
      const page = parseInt(savedPage);
      return page;
    }

    // Default to page 0
    return 0;
  });
  const [search, setSearch] = useState<string>("");
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const hasInitialized = useRef(false);
  const [loadingCartItemIds, setLoadingCartItemIds] = useState<Set<string>>(new Set());
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const [cartDataLoaded, setCartDataLoaded] = useState(false);


  // Cart URL state
  const [cartUrl, setCartUrl] = useState<string>("");

  // Venue types hierarchy state
  const [venueTypesHierarchy, setVenueTypesHierarchy] = useState<any[]>([]);

  const filtersRef = useRef(filters);

  // Load venue types hierarchy from JSON file
  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const { data } = await client.query({
          query: PROPERTY_LIST_VENUE_TYPE_LIST,
          fetchPolicy: "network-only", // optional: always fetch fresh data
        });

        if (data?.venueTypes) {
          setVenueTypesHierarchy(data.venueTypes);
        } else {
          console.error("No venue types found in response");
        }
      } catch (err) {
        console.error("Failed to load venue types hierarchy:", err);
      }
    };

    fetchVenueTypes();
  }, []);

  // Fetch cart data early for country initialization
  useEffect(() => {
    // This prevents resetting when user navigates back from detail pages
    if (!localStorage.getItem('selectedCountryId')) {
      countryInitialized.current = false;
    } else {
    }
    // Reset country initialization state on page load to ensure fresh initialization
    countryInitialized.current = false;
    fetchCartCountDetails();
  }, []);


  // Check for draft campaign data in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const campaignId = urlParams.get('draftId');
    const campaignNameParam = urlParams.get('campaignName');
    const editFlag = urlParams.get('edit') === 'true';
    const sourceParam = urlParams.get('source') || '';
    if (editFlag && campaignId && campaignNameParam) {
      setCampaignId(campaignId);
      setCampaignName(campaignNameParam);
      setEdit(editFlag); // Set edit mode to true for draft campaigns
      setSource(sourceParam);
      // setCartUrl(`${base_path}/mycart?draftId=${campaignId}&draftName=${campaignName || ''}&startDate=${startDate || ''}&endDate=${endDate || ''}&source=${source}&edit=${edit}`);
      const fetchInvoice = async () => {
        const invoice = await fetchInvoiceDetails(campaignId);

        // Extract and set country from draft invoice to prevent reset
        if (invoice?.paymentInvoice?.priceSummary?.currency?.code) {
          const draftCountryCode = invoice.paymentInvoice.priceSummary.currency.code;
          localStorage.setItem('cartCountry', draftCountryCode);
          setCartCountry(draftCountryCode);
        } else {
          console.warn('No currency code found in invoice. Full invoice structure:', JSON.stringify(invoice, null, 2));
        }

        // Only update campaignName if invoice.name is present and different from URL param
        if (invoice?.name && invoice.name !== campaignNameParam) {
          setCampaignName(invoice.name);
        }
        setStartDate(invoice?.startDate?.date ? new Date(invoice.startDate.date) : new Date());
        setEndDate(invoice?.endDate?.date ? new Date(invoice.endDate.date) : new Date());
        setDraftData(invoice);
      };
      fetchInvoice();
    } else {
      setCartUrl(`${base_path}/mycart`);
    }
  }, []);

  useEffect(() => {
    // Fetch campaignName from URL if available
    const urlParams = new URLSearchParams(window.location.search);
    const campaignNameParam = urlParams.get('campaignName');
    if (campaignNameParam) {
      setCampaignName(decodeURIComponent(campaignNameParam));
    }
  }, []);

  useEffect(() => {
    if (edit && campaignId && campaignName) {
      // Format dates consistently (yyyy-MM-dd) to match cart.tsx expectations
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';

      setCartUrl(`${base_path}/mycart?draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&source=${encodeURIComponent(source)}&edit=true`);

    }
  }, [campaignName, campaignId, startDate, endDate, source, edit, base_path]);

  // Function to get all children and grandchildren for a parent venue type
  const getAllChildrenForVenueType = (parentName: string): string[] => {
    const parent = venueTypesHierarchy.find(item => item.value === parentName);
    if (!parent) return [];

    const allChildren: string[] = [];

    const extractChildren = (item: any) => {
      if (item.child && Array.isArray(item.child)) {
        item.child.forEach((child: any) => {
          allChildren.push(child.value);
          extractChildren(child); // Recursively get grandchildren
        });
      }
    };

    extractChildren(parent);
    return allChildren;
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Persist both billboard type and current page to localStorage
  useEffect(() => {
    localStorage.setItem('proBillboardType', billboardType);
  }, [billboardType]);

  useEffect(() => {
    localStorage.setItem('propcurrentPage', currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem('proSpotBasedBooking', spotBasedBooking.toString());
  }, [spotBasedBooking]);

  useEffect(() => {
    localStorage.setItem('proBuyingFormat', buyingFormat);
  }, [buyingFormat]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Close all dropdowns if click is outside any dropdown container
      if (!target.closest('[data-dropdown="country"]')) {
        setShowCountryDropdown(false);
      }
      if (!target.closest('[data-dropdown="state"]')) {
        setShowStateDropdown(false);
      }
      if (!target.closest('[data-dropdown="district"]')) {
        setShowDistrictDropdown(false);
      }
      if (!target.closest('[data-dropdown="mediaOwner"]')) {
        setShowMediaOwnerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update billboard and package isInCart flags when cartDataList changes
  useEffect(() => {
    // Create separate sets for different item types
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType !== 'package') // Regular billboards (digital/classic)
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    const cartPackageIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType === 'package') // Package items
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Update billboard data with cart status
    setBillBoardData((prevData: any[]) =>
      prevData.map(billboard => ({
        ...billboard,
        isInCart: cartBillboardIds.has(billboard.id)
      }))
    );

    setFilteredBillboards((prev: any[]) =>
      prev.map(billboard => ({
        ...billboard,
        isInCart: cartBillboardIds.has(billboard.id)
      }))
    );

    // Update package data with cart status
    setPackageData((prevData: any[]) =>
      prevData.map(pkg => ({
        ...pkg,
        isInCart: cartPackageIds.has(pkg.id)
      }))
    );
  }, [cartDataList]);




  // GraphQL queries
  // Country filter query
  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure },
    fetchPolicy: 'cache-first',
    onError: (error) => {
      console.error('Country Filter API Error:', error);
    }
  });



  // Country options mapping
  const countryOptions = (() => {
    if (countryError) {
      console.error('Error loading countries:', countryError);
      return [];
    }
    if (countryLoading) {
      return [];
    }
    if (!countryData?.getMarketplaceCountries) {
      return [];
    }

    // Sort countries alphabetically by name before mapping
    return countryData.getMarketplaceCountries
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((country: any) => ({
        label: country.name,
        value: country.id, // Use the id field instead of countryId
        countryId: country.countryId, // Keep countryId for reference
        id: country.id,
        iso: country.iso, // Include ISO code for reference
        tax: country.tax, // Include tax information for cart context
        currencyCode: country.currencyCode // Include currency code for cart context
      }));
  })();

  // ================= Country Resolution Helpers =================
  type CountryOption = {
    label: string;
    value: string; // Mongo _id
    countryId: string;
    id: string;
    iso?: string;
    tax?: any;
    currencyCode?: string;
  };

  // Derive country from cart inventories (first item's country fields)
  const deriveCountryFromCart = (cartDataList: any[], options: CountryOption[]): CountryOption | null => {
    const firstInventory = cartDataList[0]?.campaignInventories?.[0];
    let raw = firstInventory?.billboardCountry || firstInventory?.cartItemCountry || firstInventory?.country;
    // Fallback to stored cartCountry if inventories not yet populated
    if (!raw) {
      raw = localStorage.getItem('cartCountry') || cartCountry;
    }
    if (!raw) return null;
    const lowered = String(raw).toLowerCase();
    return options.find(c =>
      c.label.toLowerCase() === lowered ||
      c.countryId.toLowerCase() === lowered ||
      (c.iso && c.iso.toLowerCase() === lowered) ||
      c.value === raw ||
      c.id === raw
    ) || null;
  };

  // Resolve selected country based on priority Cart > manual selection > session default > first option
  const resolveSelectedCountry = (
    cartDataList: any[],
    options: CountryOption[],
    storedSelectedCountryId: string | null,
    sessionDefaultCountryId: string | null
  ): CountryOption | null => {
    // Priority 1: Cart
    const cartCountry = deriveCountryFromCart(cartDataList, options);
    if (cartCountry) return cartCountry;

    // Priority 2: Manual selection (selectedCountryId in localStorage)
    if (storedSelectedCountryId) {
      const manual = options.find(c => c.id === storedSelectedCountryId || c.value === storedSelectedCountryId);
      if (manual) return manual;
    }

    // Priority 3: Session default
    if (sessionDefaultCountryId) {
      const session = options.find(c => c.id === sessionDefaultCountryId || c.value === sessionDefaultCountryId);
      if (session) return session;
    }

    // Priority 4: First available
    return options[0] || null;
  };

  // Effective country id for API calls with explicit override param
  const getEffectiveCountryId = (explicit?: string): string => {
    // Highest precedence: explicit override
    if (explicit) return explicit;

    // Prefer raw cart country in storage even before inventories load
    const rawCart = localStorage.getItem('cartCountry');
    if (rawCart && countryOptions.length > 0) {
      const lowered = rawCart.toLowerCase();
      const cartOpt = (countryOptions as CountryOption[]).find(c =>
        c.label.toLowerCase() === lowered ||
        c.countryId.toLowerCase() === lowered ||
        (c.iso && c.iso.toLowerCase() === lowered)
      );
      if (cartOpt) return cartOpt.value;
    }

    // Inventories-derived cart country
    const invOpt = deriveCountryFromCart(cartDataList, countryOptions as CountryOption[]);
    if (invOpt) return invOpt.value;

    // After initialization trust selectedCountryId
    if (countryInitialized.current && selectedCountryId) return selectedCountryId;

    // Defer fetch until country resolves (return blank string -> caller should skip)
    return '';
  };

  // Track last effective country used for billboard API calls
  const previousApiCountryRef = useRef<string>('');

  // ✅ SIMPLE FIX: Sync country dropdown and inventory list on back navigation
  useEffect(() => {
    // Skip if URL parameter exists - it takes highest priority now
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('countryId')) {
      localStorage.removeItem('clearStartCampaignFilters');
      return;
    }

    const shouldSync = localStorage.getItem('clearStartCampaignFilters') === 'true';
    // 🔴 FIX: Stop this logic from running during Draft/Edit flow
    if (edit) return;

    if (!shouldSync) return;

    // Wait until countryOptions are loaded
    if (!countryOptions || countryOptions.length === 0) {
      return;
    }

    // � CRITICAL: Wait for cart data to load before proceeding
    // This prevents premature API calls with default country when cart exists
    if (!cartDataLoaded) {
      console.warn('⏸️ [Cart Restoration] Waiting for cart data to load before restoration');
      return;
    }

    // 🔒 Set lock to prevent other effects from running
    cartRestorationInProgress.current = true;

    localStorage.removeItem('clearStartCampaignFilters');

    // At this point, we've confirmed NO cart data exists
    // Safe to use session default country
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
    const newCountryId = sessionDefaultCountryId;
    
    console.info('🌍 [Cart Restoration] No cart data - using session default country:', { countryId: newCountryId });

    if (!newCountryId) {
      console.warn('⚠️ [Cart Restoration] No country found, releasing lock');
      cartRestorationInProgress.current = false;
      return;
    }

    // Sync dropdown and filters
    setSelectedCountryId(newCountryId || '');
    setFilters((prev: any) => ({
      ...prev,
      country: newCountryId || '',
    }));

    // Refetch inventory list for the new country
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // 🔒 Only fetch if we have a valid country ID resolved
    if (newCountryId) {
      console.log('🔄 [Cart Restoration] Fetching billboards with country:', newCountryId);
      fetchAllBillboardData('', 0, cartBillboardIds, undefined, [], undefined, newCountryId ?? undefined);
    } else {
      console.warn('⏸️ [Cart Restoration] Skipping API call - country not resolved');
    }

  }, [cartDataList, countryOptions, edit, cartDataLoaded, cartCountry]);

  // ✅ Sync country dropdown and billboard list when returning from detail page
  useEffect(() => {
    // Skip if URL parameter exists - it takes highest priority now
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('countryId')) {
      localStorage.removeItem('navigationCountryBackup');
      return;
    }

    const navigationBackup = localStorage.getItem('navigationCountryBackup');

    if (navigationBackup && countryOptions.length > 0 && !edit && cartDataLoaded) {
      try {
        const backup = JSON.parse(navigationBackup);

        // If backup is recent (within 1 hour)
        if (backup.timestamp > Date.now() - 3600000) {
          const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);

          if (backupCountry) {

            // ✅ FIX: Mark initialization to prevent other effects from overriding
            countryInitialized.current = true;

            // Sync dropdown with backup country and restore filters
            setSelectedCountryId(backupCountry.value);
            setFilters((prev: any) => ({
              ...prev,
              country: backupCountry.value,
              state: backup.state || [],        // Restore State
              district: backup.district || [],  // Restore District
              // ✅ Restore media owner and environment from backup
              mediaOwner: backup.mediaOwner || [],
              environment: backup.environment || ''
            }));

            // Reset location states
            setState(backup.state || []);       // Restore State Variable
            setDistrict(backup.district || []);
            // setAvailableDistricts([]);

            // Fetch billboards for the backed-up country
            const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
              cart.campaignInventories?.map((inv: any) => inv.billboardId)
            ).filter(Boolean));

            // Pass backup country explicitly to API
            fetchAllBillboardData(search, 0, cartBillboardIds, undefined, backup.state || [], undefined, backupCountry.value);

            // ✅ FIX: Clean up backup AFTER state is set to prevent re-triggering
            setTimeout(() => {
              localStorage.removeItem('navigationCountryBackup');
            }, 100);
          } else {
            localStorage.removeItem('navigationCountryBackup');
          }
        } else {
          // Backup expired, clean it up
          localStorage.removeItem('navigationCountryBackup');
        }
      } catch (e) {
        console.error('Error parsing navigation backup:', e);
        localStorage.removeItem('navigationCountryBackup');
      }
    }
  }, [countryOptions.length, cartDataLoaded, edit]);

  // Get MongoDB id for API calls (use the selected country's MongoDB id directly)
  // const selectedCountryForStates = countryOptions.find((c: any) => c.value === filters.country);
  // Fallback: if no country selected, find by countryIdData from localStorage
  // const fallbackCountry = !selectedCountryForStates && countryIdData ?
  //   countryOptions.find((c: any) => c.countryId === countryIdData) : null;
  // const countryIdForStates = selectedCountryForStates?.value || fallbackCountry?.value; // Always use MongoDB id


  const fetchCartCountDetails = async () => {
    // Skip if already fetching to prevent duplicate calls
    if (cartCountRequestInProgress.current) {
      return 0; // Return 0 instead of undefined
    }

    cartCountRequestInProgress.current = true;
    try {
      let validStartDate = startDate;
      let validEndDate = endDate;
      setCartDataLoaded(false); // Mark cart data as not loaded

      // NOTE: Do not clear cartCountry pre-fetch; keep previous value until new data arrives to allow proper initialization.

      // If dates are strings, convert to Date objects
      if (typeof validStartDate === 'string') validStartDate = new Date(validStartDate);
      if (typeof validEndDate === 'string') validEndDate = new Date(validEndDate);

      if (!isValid(validStartDate) || !isValid(validEndDate)) {
        console.error("Invalid dates detected:", { startDate, endDate });
        throw new Error("Invalid date values for cart count fetch.");
      }

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        try {
          const mockData = await generateMockData(GET_MY_CART_ITEMS);

          // Check if this is a draft flow - skip setting cartCountry for drafts
          const urlParams = new URLSearchParams(window.location.search);
          const isDraft = urlParams.get('edit') === 'true';

          if (mockData.data?.cartpageItem?.length > 0) {
            const cartCountry = mockData.data.cartpageItem[0].cartItemCountry;
            if (cartCountry && !isDraft) { // Only set for non-drafts
              localStorage.setItem('cartCountry', cartCountry.toLowerCase());
              setCartCountry(cartCountry.toLowerCase());
            } else if (isDraft) {
              console.info('🛒 Skipping cart country set for draft - will be handled by start-campaign');
            }
          } else {
            if (!isDraft) {
              localStorage.removeItem('cartCountry');
              setCartCountry('');
              countryInitialized.current = false; // allow fallback if needed later
            }
          }

          // Count the items from cartpageItem array for mock data
          const count = mockData.data?.cartpageItem?.length || 0;
          setCartCount(count);
          setCartItemCountContext(count); // Sync with cart context
          window.dispatchEvent(new Event('cartUpdated'));
          setCartDataLoaded(true); // Mark cart data as loaded
          return count;
        } catch (error) {
          console.error("Error generating mock cart data:", error);
          // Clear cart country on error to ensure fallback to default country
          localStorage.removeItem('cartCountry');
          localStorage.removeItem('selectedCountryId');

          // Reset country initialization to allow re-initialization with default country
          countryInitialized.current = false;
          setCartCount(0);
          setCartItemCountContext(0);
          setCartDataLoaded(true); // Mark cart data as loaded
          return 0;
        }
      } else {
        try {
          const { data } = await client.query({
            query: GET_MY_CART_ITEMS, // Query for fetching cart items
            fetchPolicy: "network-only",
            variables: {
              accessToken: token,
              userId: userId,
            }
          });

          // Store cart country data for unified initialization
          // Check if this is a draft flow - skip setting cartCountry for drafts
          const urlParams = new URLSearchParams(window.location.search);
          const isDraft = urlParams.get('edit') === 'true';

          if (data?.cartpageItem?.length > 0) {
            const cartCountry = data.cartpageItem[0].cartItemCountry;
            if (cartCountry && !isDraft) { // Only set for non-drafts
              localStorage.setItem('cartCountry', cartCountry.toLowerCase());
              setCartCountry(cartCountry.toLowerCase());
            } else if (isDraft) {
              console.info('🛒 Skipping cart country set for draft - will be handled by start-campaign');
            }
          } else {
            if (!isDraft) {
              localStorage.removeItem('cartCountry');
              setCartCountry('');
              // countryInitialized.current = false;
            }
          }

          // Count the items from cartpageItem array
          const count = data?.cartpageItem?.length || 0;
          setCartCount(count);
          setCartItemCountContext(count); // Sync with cart context
          window.dispatchEvent(new Event('cartUpdated'));
          setCartDataLoaded(true); // Mark cart data as loaded
          return count;
        } catch (error) {
          console.error("Error fetching cart count:", error);
          // On API error, don't nuke user selection; just allow re-init if cart state changes
          localStorage.removeItem('cartCountry');
          countryInitialized.current = false;

          toast({
            title: "Error",
            description: "Failed to fetch cart count.",
            variant: "destructive"
          });
          setCartCount(0);
          setCartItemCountContext(0);
          setCartDataLoaded(true); // Mark cart data as loaded
          return 0;
        }
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
      // General error: remove cartCountry only
      localStorage.removeItem('cartCountry');
      countryInitialized.current = false;

      toast({
        title: "Error",
        description: "Failed to fetch cart count.",
        variant: "destructive"
      });
      setCartCount(0);
      setCartItemCountContext(0);
      setCartDataLoaded(true); // Mark cart data as loaded
      return 0;
    } finally {
      // Always reset the in-progress flag
      cartCountRequestInProgress.current = false;
    }
  };

  const draftEffectRan = useRef(false);
  const [draftCountrySet, setDraftCountrySet] = useState(false); // Track if draft country has been set
  const dataFetchedForDraft = useRef(false); // Prevent multiple fetches for drafts

  // Reset draft data fetch flag when entering/re-entering draft mode
  useEffect(() => {
    if (edit && campaignId) {
      dataFetchedForDraft.current = false;
    }
  }, [edit, campaignId]);

  // Dedicated effect to set draft country when both draftData and countryOptions are available
  useEffect(() => {

    if (edit && draftData && countryOptions.length > 0 && !draftEffectRan.current) {
      draftEffectRan.current = true;
      let draftCountryCode = cartCountry;

      // If cartCountry is not set, try to extract from draftData directly
      if (!draftCountryCode) {
        draftCountryCode = draftData.paymentInvoice?.priceSummary?.currency?.code ||
          draftData.currency?.code ||
          draftData.priceSummary?.currency?.code;
      }

      if (draftCountryCode) {
        const draftCountry = countryOptions.find((c: any) => c.currencyCode === draftCountryCode);
        if (draftCountry) {
          setSelectedCountryId(draftCountry.id);
          setFilters((prev: any) => ({
            ...prev,
            country: draftCountry.value
          }));
          localStorage.setItem('selectedCountryId', draftCountry.id);
          if (draftCountry.tax) {
            updateTaxInfo(draftCountry.tax.label || 'Tax', draftCountry.tax.percent || 0);
          }
          countryInitialized.current = true; // Mark as initialized to prevent unified initialization override
          // draftCountrySet.current = true;
          setDraftCountrySet(true);
          // Set cartCountry to the draft country's name for consistency
          localStorage.setItem('cartCountry', draftCountry.label);
          setCartCountry(draftCountry.label);
        } else {
          // Try fallback matching
          const fallbackCountry = countryOptions.find((c: any) =>
            c.countryId === draftCountryCode ||
            c.iso === draftCountryCode ||
            c.id === draftCountryCode
          );
          if (fallbackCountry) {
            setSelectedCountryId(fallbackCountry.id);
            setFilters((prev: any) => ({
              ...prev,
              country: fallbackCountry.value
            }));
            localStorage.setItem('selectedCountryId', fallbackCountry.id);
            if (fallbackCountry.tax) {
              updateTaxInfo(fallbackCountry.tax.label || 'Tax', fallbackCountry.tax.percent || 0);
            }
            countryInitialized.current = true;
            setDraftCountrySet(true);
          } else {
            console.error('No fallback country found either. Draft will use default country.');
          }
        }
      } else {
        console.warn('No draft country code found in cartCountry or draftData');
      }
      // Mark draft initialization as complete
      setDraftInitialized(true);
    }
  }, [edit, draftData, countryOptions, cartCountry]); // Runs when draftData or countryOptions or cartCountry change

  // ✅ Simplified unified country initialization using helpers (Cart > manual > sessionDefault > first)
  useEffect(() => {
    const isDraft = new URLSearchParams(window.location.search).get('edit') === 'true';
    if (isDraft) return;

    // Check if cart conflict was resolved - force re-initialization
    const forceReinit = localStorage.getItem('forceCountryReinitialization');
    if (forceReinit === 'true') {
      countryInitialized.current = false;
      localStorage.removeItem('forceCountryReinitialization');
    }

    if (countryInitialized.current) return;
    if (countryOptions.length === 0) return;

    // ✅ SKIP if navigation backup exists - let that effect handle restoration
    const navigationBackup = localStorage.getItem('navigationCountryBackup');
    if (navigationBackup) {
      return;
    }

    // ✅ PRIORITY FIX: If returning from cart, let cart restoration effect handle it
    const returningFromCart = localStorage.getItem('clearStartCampaignFilters') === 'true';
    if (returningFromCart) {
      return;
    }

    // Wait until either cart data is loaded OR cartCountry exists (so we can prefer cart)
    const rawCartCountry = localStorage.getItem('cartCountry');
    const cartReady = cartDataLoaded || !!rawCartCountry;
    if (!cartReady) return;

    // ✅ ONLY use selectedCountryId from localStorage if user has manually changed the country
    const userChangedCountry = localStorage.getItem('userChangedCountry') === 'true';
    const storedSelectedCountryId = userChangedCountry ? localStorage.getItem('selectedCountryId') : null;
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');

    // Check if cart has items
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;

    // ✅ SCENARIO 4: Cart has items → ALWAYS use cart's country (ignore user changes & session default)
    if (cartHasItems) {
      let chosen = deriveCountryFromCart(cartDataList, countryOptions as CountryOption[]);
      if (chosen) {
        setSelectedCountryId(chosen.value);
        if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
        if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
        countryInitialized.current = true;
        return;
      }
    }

    // ✅ PRIORITY 1.5: LocalStorage has cart country (Pre-fetch fallback)
    // If cart list is empty but localStorage says we have a cart country, trust localStorage to prevent session default override
    if (!cartHasItems && rawCartCountry && !edit) {
      const lowered = rawCartCountry.toLowerCase();
      const chosen = (countryOptions as CountryOption[]).find(c =>
        c.label.toLowerCase() === lowered ||
        c.countryId.toLowerCase() === lowered ||
        (c.iso && c.iso.toLowerCase() === lowered)
      );
      if (chosen) {
        setSelectedCountryId(chosen.value);
        if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
        if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
        countryInitialized.current = true;
        return;
      }
    }

    // ✅ SCENARIO 2: User manually changed country (userChangedCountry === true) → use stored selection
    if (!cartHasItems && userChangedCountry && storedSelectedCountryId) {
      const chosen = (countryOptions as CountryOption[]).find(c =>
        c.id === storedSelectedCountryId || c.value === storedSelectedCountryId
      );
      if (chosen) {
        setSelectedCountryId(chosen.value);
        if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
        if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
        countryInitialized.current = true;
        return;
      }
    }

    // ✅ SCENARIO 1 & 3: Cart empty AND user hasn't changed country → use session default
    if (!cartHasItems && !userChangedCountry && sessionDefaultCountryId) {
      const chosen = (countryOptions as CountryOption[]).find(c =>
        c.id === sessionDefaultCountryId || c.value === sessionDefaultCountryId
      );
      if (chosen) {
        setSelectedCountryId(chosen.value);
        if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
        if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
        countryInitialized.current = true;
        return;
      }
    }

    // Fallback: use resolveSelectedCountry as last resort
    let chosen = resolveSelectedCountry(cartDataList, countryOptions as CountryOption[], storedSelectedCountryId, sessionDefaultCountryId);
    if (!chosen) return;

    setSelectedCountryId(chosen.value);
    if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
    localStorage.setItem('selectedCountryId', chosen.id);
    if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
    countryInitialized.current = true;
  }, [countryOptions.length, cartDataLoaded, cartDataList.length, filters.country, updateTaxInfo]);

  // Debug: log final initialization state once selectedCountryId stabilizes
  useEffect(() => {
    if (!countryInitialized.current) return;
  }, [selectedCountryId]);

  // Late cart sync: if cart items appear after init and differ from selectedCountryId, switch
  useEffect(() => {
    if (!countryInitialized.current) return; // only act after initial selection
    if (countryOptions.length === 0) return;
    const rawCartCountry = localStorage.getItem('cartCountry');
    if (!rawCartCountry) return;
    const lowered = rawCartCountry.toLowerCase();
    const cartOption = (countryOptions as CountryOption[]).find(c =>
      c.label.toLowerCase() === lowered ||
      c.countryId.toLowerCase() === lowered ||
      (c.iso && c.iso.toLowerCase() === lowered)
    );
    if (!cartOption) return;
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    if (cartHasItems && selectedCountryId !== cartOption.value) {
      setSelectedCountryId(cartOption.value);
      setFilters((prev: any) => ({ ...prev, country: cartOption.value }));
      if (cartOption.tax) updateTaxInfo(cartOption.tax.label || 'Tax', cartOption.tax.percent || 0);
      // Trigger immediate billboard refetch with new country to avoid showing previous country's data
      // Build current cart billboard id set for isInCart flag consistency
      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      // Avoid double fetch if one already in progress
      if (!billBoardDataLoading) {
        fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, undefined, cartOption.value);
      } else {
        console.warn('⏳ Skipping refetch (already loading). Will rely on upcoming completion.');
      }
    }
  }, [cartDataList.length, selectedCountryId, countryOptions.length, updateTaxInfo]);

  // 📝 Draft flow: If draft is active and cart has items, sync country dropdown and billboard list to cart country
  useEffect(() => {
    // Only run if in draft mode, draftData exists, and cart has items
    if (
      edit &&
      campaignId &&
      draftData &&
      cartDataList.length > 0 &&
      cartDataList[0]?.campaignInventories?.length > 0 &&
      countryOptions.length > 0
    ) {
      // Get cart country from first inventory
      const cartCountry = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
      // Find the country option object
      const cartCountryObj = countryOptions.find((c: any) => c.label === cartCountry);

      if (cartCountryObj) {
        // 🔴 FIX: Check if the country is DIFFERENT before doing anything
        if (selectedCountryId !== cartCountryObj.value) {
          setSelectedCountryId(cartCountryObj.value);
          setFilters((prev: any) => ({
            ...prev,
            country: cartCountryObj.value,
          }));

          // 🔴 MOVED INSIDE: Only fetch if we actually CHANGED the country
          const cartBillboardIds = new Set(
            cartDataList.flatMap((cart: any) =>
              cart.campaignInventories?.map((inv: any) => inv.billboardId)
            ).filter(Boolean)
          );

          if (!billBoardDataLoading) {
            fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, undefined, cartCountryObj.value);
          }
        } else {
          // 🔴 ADDED: Log that we are skipping the call because country matches
          console.warn('📝 Draft flow: Cart updated but country matches. SKIPPING API call.');
        }
      }
    }
  }, [edit, campaignId, draftData, cartDataList, countryOptions, selectedCountryId, billBoardDataLoading, updateTaxInfo, search]);


  // Enhanced navigation protection - prevent country resets during navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Preserve user's manual country selection across navigation
      if (selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('selectedCountryId', selectedCountry.id);
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now(),
            // ✅ Backup media owner filter too
            mediaOwner: filtersRef.current.mediaOwner || [],
            environment: filtersRef.current.environment || ''
          }));
        }
      }
    };

    const handleVisibilityChange = () => {
      // Also preserve on visibility change (covers more navigation scenarios)
      if (document.visibilityState === 'hidden' && selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now()
          }));
        }
      }
    };

    const handleFocus = () => {
      // When page regains focus, check for navigation backup
      const navigationBackup = localStorage.getItem('navigationCountryBackup');

      if (navigationBackup && countryOptions.length > 0) {
        try {
          const backup = JSON.parse(navigationBackup);
          // Use backup if it's recent (within 1 hour)
          if (backup.timestamp > Date.now() - 3600000) {
            const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);
            if (backupCountry && selectedCountryId !== backupCountry.value) {
              setSelectedCountryId(backupCountry.value);
              setFilters((prev: any) => ({
                ...prev,
                country: backupCountry.value
              }));
              countryInitialized.current = true;
            }
          }
        } catch (e) {
          console.error("Error parsing navigation backup on focus:", e);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedCountryId, countryOptions]);

  // 🛒 Handle cart country override only when there's a real conflict
  useEffect(() => {
    // Skip initialization for draft campaigns to prevent overriding draft country
    const urlParams = new URLSearchParams(window.location.search);
    const isDraft = urlParams.get('edit') === 'true';
    if (isDraft) {
      console.warn('📝 Skipping unified initialization for draft campaign');
      return;
    }

    // Wait for both country options and cart data to be loaded
    if (countryInitialized.current || countryOptions.length === 0 || !cartDataLoaded) {
      console.warn("⏭️ Skipping initialization - not ready yet");
      return;
    }
    // Only override if cart country exists, cart has items, and it's different from current selection
    if (cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0 && countryOptions.length > 0 && !countryInitialized.current) {
      const cartCountry = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;

      if (cartCountry) {
        const cartCountryObj = countryOptions.find((c: any) => c.label === cartCountry);

        // Only override if cart country is different from current selected country
        if (cartCountryObj && selectedCountryId !== cartCountryObj.value) {
          setSelectedCountryId(cartCountryObj.value);
          setFilters((prev: any) => ({
            ...prev,
            country: cartCountryObj.value
          }));

          // Update tax info
          if (cartCountryObj.tax) {
            const taxLabel = cartCountryObj.tax.label || 'Tax';
            const taxPercent = cartCountryObj.tax.percent || 0;
            updateTaxInfo(taxLabel, taxPercent);
          }
        }
      }
    }
  }, [cartDataList.length, countryOptions, updateTaxInfo, edit]);

  const { data: stateData, loading: stateApiLoading, refetch: refetchStates } = useQuery(GET_STATE_LIST_MARKETPLACE, {
    variables: { countryIds: selectedCountryId, exposure: exposure },
    skip: !selectedCountryId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    fetchPolicy: 'network-only',
  });

  // Refetch states when country changes
  useEffect(() => {
    if (selectedCountryId && !edit && refetchStates) {
      refetchStates();
    }
  }, [selectedCountryId, refetchStates, edit]);

  const [getDistricts, { data: districtData, loading: districtApiLoading }] = useLazyQuery(GET_DISTRICT_LIST_MARKETPLACE);

  const isPageLoading = (filters.category === 'packages' ? packageDataLoading : billBoardDataLoading) || stateApiLoading || districtApiLoading || countryLoading;

  // Removed conflicting localStorage useEffect - now handled in unified initialization above

  // Multi-select dropdown states for country, state, district
  // Sync filters.country with selectedCountryId
  useEffect(() => {
    // Avoid unnecessary state updates if already matched
    setFilters((prev: any) => {
      if (prev.country === selectedCountryId) return prev;
      return { ...prev, country: selectedCountryId };
    });
  }, [selectedCountryId]);

  // Sync filters.state and filters.district with state and district variables
  // Also persist to localStorage like mediaOwner dropdown
  useEffect(() => {
    setFilters((prev: any) => ({
      ...prev,
      state,
      district,
    }));

    // Save state to localStorage
    if (state.length > 0) {
      localStorage.setItem('campaignState', state[0]);
    }

    // Save district to localStorage
    if (district.length > 0) {
      localStorage.setItem('campaignDistrict', district[0]);
    }
  }, [state, district]);

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

  // Search input states for dropdowns
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');
  const [mediaOwnerSearchTerm, setMediaOwnerSearchTerm] = useState('');
  const [showMediaOwnerDropdown, setShowMediaOwnerDropdown] = useState(false);

  // Mock data generation for states
  const generateMockStates = async () => {
    try {
      setStatesLoading(true);
      const mockResponse = await generateMockData(GET_STATE_LIST);
      const states = mockResponse.data.stateList || [];
      setAvailableStates(states);
    } catch (error) {
      console.error('Error generating mock states:', error);
      toast({
        title: "Error",
        description: "Failed to load mock state data.",
        variant: "destructive"
      });
      // toast.error('Failed to load mock state data.');
      setAvailableStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  // Mock data generation for districts
  const generateMockDistricts = async () => {
    try {
      setDistrictsLoading(true);
      const mockResponse = await generateMockData(GET_DISTRICT_LIST_MARKETPLACE);
      const districts = mockResponse.data.districtListMarketplace || [];
      setAvailableDistricts(districts);
    } catch (error) {
      console.error('Error generating mock districts:', error);
      toast({
        title: "Error",
        description: "Failed to load mock district data.",
        variant: "destructive"
      });
      // toast.error('Failed to load mock district data.');
      setAvailableDistricts([]);
    } finally {
      setDistrictsLoading(false);
    }
  };

  // Helper functions to filter dropdown options based on search term
  const filteredCountryOptions = countryOptions.filter((country: any) =>
    country.label.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const filteredStateOptions = availableStates.filter((state: any) => {
    const stateName = state.name || state.stateName;
    return stateName.toLowerCase().includes(stateSearchTerm.toLowerCase());
  });

  const filteredDistrictOptions = availableDistricts.filter((district: any) => {
    const districtName = district.name || district.districtName;
    return districtName.toLowerCase().includes(districtSearchTerm.toLowerCase());
  });

  const filteredMediaOwnerOptions = mediaOwnerOptions.filter((owner: any) =>
    owner.label.toLowerCase().includes(mediaOwnerSearchTerm.toLowerCase())
  );

  // Fetch states on mount
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockStates();
    } else {
      if (stateData?.stateListMarketplace) {
        setAvailableStates(stateData.stateListMarketplace);
      }
      setStatesLoading(stateApiLoading);
    }
  }, [stateData, stateApiLoading]);

  // Fetch districts when state changes
  useEffect(() => {
    if (!state.length) {
      setAvailableDistricts([]);
      setDistrict([]);
      updateFilter('district', [], true);
      return;
    }
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockDistricts();
    } else {
      getDistricts({ variables: { stateIds: Array.from(new Set(state)).filter(Boolean).join(','), exposure: exposure }, fetchPolicy: 'network-only' });
    }
  }, [state, getDistricts]);

  // Update districts when district data changes
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && districtData?.districtListMarketplace) {
      setAvailableDistricts(districtData.districtListMarketplace);
      setDistrictsLoading(districtApiLoading);
    }
  }, [districtData, districtApiLoading]);

  // Reset state and district when country changes
  useEffect(() => {
    // Skip if country not initialized yet or during draft/edit mode
    if (!countryInitialized.current || edit) return;

    if (localStorage.getItem('navigationCountryBackup')) {
      return;
    }

    // Only reset if we have a valid country selected
    if (!selectedCountryId) return;

    // Reset state and district selections
    setState([]);
    setDistrict([]);
    setAvailableDistricts([]);

    // Update filters to clear state/district
    setFilters((prev: any) => ({
      ...prev,
      state: [],
      district: [],
    }));

    // Clear localStorage
    localStorage.removeItem('campaignListState');

  }, [selectedCountryId, edit]);

  const flowType: 'create-campaign' | 'edit-campaign' | 'explore' = 'create-campaign';

  // Explore flow has been removed


  // First, declare fetchAllBillboardData function with loading guard
  const fetchAllBillboardData = async (searchValue?: string, page?: number, cartBillboardIds = new Set<string>(), overrideBillboardType?: string, overrideState?: string[], overridePriceRange?: [number, number] | { minPrice?: number; maxPrice?: number }, countryId?: string, overrideDistrict?: string[], overrideCompanyId?: string, overrideVenueType?: string[], overrideEnvironment?: string) => {
    // Use actual values if provided, otherwise use current state
    const effectiveSearchValue = searchValue !== undefined ? searchValue : search;
    const effectivePage = page !== undefined ? page : currentPage;


    // Unified effective country resolution
    const effectiveCountryId = getEffectiveCountryId(countryId);

    if (!effectiveCountryId || countryOptions.length === 0) {
      console.log('⏸️ Skipping fetchAllBillboardData - no country ID or options not loaded');
      return;
    }

    // � CRITICAL VALIDATION: If cart has data, ONLY allow cart country API calls
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    const rawCart = localStorage.getItem('cartCountry');
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
    
    // Check if this is a default country call while cart exists
    const isDefaultCountryCall = effectiveCountryId === sessionDefaultCountryId;
    const cartExists = cartHasItems || rawCart || cartCountry;
    
    if (cartExists && isDefaultCountryCall) {
      console.error('🚫 BLOCKED: Attempted default country API call while cart exists!', {
        effectiveCountryId,
        sessionDefaultCountryId,
        cartHasItems,
        rawCart,
        cartCountry
      });
      return; // BLOCK the call
    }

    // 🔍 Log which country is being used for the API call
    if (cartHasItems || rawCart) {
      console.log('🛒 [API Call] Using CART country:', { effectiveCountryId, cartHasItems, rawCart });
    } else {
      console.log('🌍 [API Call] Using selected/default country:', { effectiveCountryId });
    }

    // ✅ Deduplication: Skip if same fetch already requested recently (within last 1 second)
    const effectiveBillboardType = overrideBillboardType || billboardType;

    if (effectiveBillboardType === 'packages' || (filters.category === 'packages' && !overrideBillboardType)) {
      setBillBoardDataLoading(false);
      return;
    }

    const now = Date.now();
    if (
      lastBillboardFetchRef.current.countryId === effectiveCountryId &&
      lastBillboardFetchRef.current.page === effectivePage &&
      lastBillboardFetchRef.current.type === effectiveBillboardType &&
      lastBillboardFetchRef.current.timestamp &&
      now - lastBillboardFetchRef.current.timestamp < 1000 // Within 1 second
    ) {
      return;
    }

    // Update the last fetch ref
    lastBillboardFetchRef.current = {
      countryId: effectiveCountryId,
      page: effectivePage,
      type: effectiveBillboardType,
      timestamp: now
    };

    // Prevent multiple simultaneous calls
    if (billBoardDataLoading) {
      return;
    }

    const effectiveState = overrideState !== undefined ? overrideState : state;
    const effectiveDistrict = overrideDistrict !== undefined ? overrideDistrict : district;
    const stateIdString = Array.from(new Set(effectiveState)).filter(Boolean).join(',');

    // Check for price-only filtering (when overridePriceRange is provided, allow empty state)
    const isPriceOnlyFilter = overridePriceRange && (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange));

    // Allow API call when country changes (overrideState is explicitly passed as empty array)
    const isCountryChange = overrideState !== undefined && overrideState.length === 0;

    // Skip API call only if state is empty AND other filters are applied (but NOT during country change)
    if (!stateIdString && !isCountryChange && (district.length > 0 || (filters.priceRange && !isPriceOnlyFilter))) {
      setNoDataMessage('Please select at least one state to apply filters.');
      return;
    }

    try {
      setBillBoardDataLoading(true);
      setNoDataMessage(null);

      const effectiveStartDate = startDate;
      const effectiveEndDate = endDate;

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        try {
          const mockResponse = await generateMockData(GETPROPERTY_LIST);

          if (!mockResponse || !mockResponse.data) {
            console.error("Mock data response is null or missing data property");
            setNoDataMessage('Mock data failed to load.');
            setBillBoardDataLoading(false);
            return;
          }

          const allProperties = mockResponse.data.landingpageExploreProperties || [];

          if (allProperties.length === 0) {
            console.error("Mock data returned empty array");
            setNoDataMessage('No mock data available.');
            setBillBoardDataLoading(false);
            return;
          }
          const classicProperties = allProperties.filter((p: any) =>
            p.type?.toUpperCase() === 'CLASSIC' || p.type?.toUpperCase() === 'STATIC'
          );
          const digitalProperties = allProperties.filter((p: any) =>
            p.type?.toUpperCase() === 'DIGITAL'
          );

          // Update count for current billboard type only
          const effectiveBillboardType = overrideBillboardType || billboardType;
          if (effectiveBillboardType.toLowerCase() === 'classic') {
            setclasicTotalSize(classicProperties.length);
            // Keep existing digital count, don't update it
          } else {
            setdigitalTotalSize(digitalProperties.length);
            // Keep existing classic count, don't update it
          }

          const filteredProperties = allProperties.filter((property: any) => {
            const propertyType = property.type?.toUpperCase();
            const targetType = effectiveBillboardType.toUpperCase();
            const matchesState = !effectiveState.length || effectiveState.includes(property.stateId);
            const matchesDistrict = !district.length || district.includes(property.districtId);
            return (propertyType === targetType || (targetType === 'CLASSIC' && propertyType === 'STATIC')) &&
              matchesState && matchesDistrict;
          });

          const startIndex = effectivePage * dataListLength;
          const endIndex = Math.min(startIndex + dataListLength, filteredProperties.length);
          const paginatedData = filteredProperties.slice(startIndex, endIndex);

          const billboardsWithCartState = paginatedData.map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }));

          setBillBoardData(billboardsWithCartState);
          setTotalDataLength(filteredProperties.length);

          if (filteredProperties.length > 0) {
            setPageStartData(startIndex + 1);
            setPageEndData(endIndex);
          } else {
            setPageStartData(0);
            setPageEndData(0);
            setNoDataMessage(
              district
                ? `No ${billboardType} inventoires found for the selected district.`
                : effectiveState.length
                  ? `No ${billboardType} inventoires found for the selected state.`
                  : `No ${billboardType} inventoires found for your filters.`
            );
          }

          // Update filteredBillboards with the top 5 from the filtered properties
          const topBillboards = filteredProperties
            .slice(0, 5)
            .map((billboard: any) => ({
              ...billboard,
              isInCart: cartBillboardIds.has(billboard.id),
            }));
          setFilteredBillboards(topBillboards);

          if (viewMode === 'map' && map) {
            setTimeout(() => {
              if (map) {
                map.resize();
                map.flyTo({
                  center: [153.0251, -27.4698],
                  zoom: 5,
                  essential: true,
                });
              }
            }, 100);
          }
        } catch (error) {
          console.error("Error in mock data processing:", error);
          setNoDataMessage('Failed to load mock data.');
          setBillBoardDataLoading(false);
          return;
        }
      } else {
        // Similar logic for API data, ensuring state and district filters are applied
        const categories = locationData.map(category => category.toUpperCase());
        // Use ref value instead of filters.environment to get current value
        const currentEnvironment = currentEnvironmentRef.current;
        if (currentEnvironment && currentEnvironment !== '') {
          categories.push(currentEnvironment.toUpperCase());
        }
        const filter = {
          availableBooking: true,
          categories: categories,
          format: siteTypeData,
          venueType: overrideVenueType !== undefined ? overrideVenueType : currentVenueTypeRef.current,
        };

        // Use selected media owner ID as companyId from filtersRef (always up-to-date), or empty string if no media owner selected
        const selectedCompanyId = (overrideCompanyId !== undefined)
          ? overrideCompanyId
          : (filtersRef.current.mediaOwner && filtersRef.current.mediaOwner.length > 0
            ? filtersRef.current.mediaOwner.join(',') // Use filtersRef for most current value
            : ""); // Send empty string when no media owner is selected
        const freshCartCount = await fetchCartCountDetails();
        const hasCartData = freshCartCount > 0 || (cartDataList && cartDataList.length > 0); 

        // Determine finalCountryId: Cart country > selectedCountryId > countryId
        let finalCountryId = countryId || selectedCountryId;

        // ✅ CONSOLIDATED LOGIC: If cart has items, ALWAYS use cart's country
        const userChangedCountry = localStorage.getItem('userChangedCountry') === 'true';
        const rawCartCountry = localStorage.getItem('cartCountry');
        const isClearAllAction = localStorage.getItem('clearedAllCart') === 'true';

        // Priority 1: Cart Has Data OR LocalStorage has Cart Country (and not clearing/editing)
        if (hasCartData || (rawCartCountry && !edit && !isClearAllAction)) {

          let foundCartCountry = false;

          // Try cartDataList first (if populated)
          if (cartDataList.length > 0) {
            const cartCountryObj = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
            if (cartCountryObj) {
              const cartCountryOption = countryOptions.find((c: any) => c.label === cartCountryObj);
              if (cartCountryOption) {
                finalCountryId = cartCountryOption.value;
                foundCartCountry = true;
              }
            }
          }

          // Fallback: Try matching rawCartCountry string from localStorage if dataList missed it
          if (!foundCartCountry && rawCartCountry) {
            const lowered = rawCartCountry.toLowerCase();
            const cartCountryOption = countryOptions.find((c: any) =>
              c.label.toLowerCase() === lowered ||
              c.countryId.toLowerCase() === lowered ||
              (c.iso && c.iso.toLowerCase() === lowered)
            );
            if (cartCountryOption) {
              finalCountryId = cartCountryOption.value;
            }
          }
        }
        // Priority 2: Edit Mode
        else if (edit) {
          const currentSelected = localStorage.getItem('selectedCountryId');
          if (currentSelected) {
            finalCountryId = currentSelected;
          }
        }
        // Priority 3: Clear All Flag (SCENARIO 4)
        else if (localStorage.getItem('clearedAllCart') === 'true') {
          // 🔄 Clear All clicked - Reset to session default
          const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
          if (sessionDefaultCountryId) {
            finalCountryId = sessionDefaultCountryId;
            localStorage.setItem('selectedCountryId', sessionDefaultCountryId);
            localStorage.setItem('userChangedCountry', 'false'); // Reset flag
          }
          // Clean up the flag after using it
          localStorage.removeItem('clearedAllCart');
        }
        // Priority 4: User Selection (SCENARIO 3)
        else if (userChangedCountry) {
          const userSelectedCountry = localStorage.getItem('selectedCountryId');
          if (userSelectedCountry) {
            finalCountryId = userSelectedCountry;
          }
        }
        // Priority 5: Fallback to session default (SCENARIO 2)
        else {
          const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
          if (sessionDefaultCountryId) {
            finalCountryId = sessionDefaultCountryId;
          }
        }
        // Get preserved state and district from localStorage or use current values
        const preservedState = overrideState !== undefined ? overrideState : state;
        const preservedDistrict = overrideDistrict !== undefined ? overrideDistrict : district;

        const commonVariables = {
          accessToken: token,
          sort: "last_modified_date,desc",
          userId: userId,
          companyId: selectedCompanyId, // Use selected media owner ID or empty string
          page: effectivePage,
          size: dataListLength,
          startDate: safeFormatDate(effectiveStartDate, 'dd MMMM yyyy'),
          endDate: safeFormatDate(effectiveEndDate, 'dd MMMM yyyy'),
          dspName: exposure,
          countryId: finalCountryId, // Use sessionDefaultCountryId if cart is empty, else old value
          stateId: currentStateRef.current && currentStateRef.current.length > 0
            ? Array.from(new Set(currentStateRef.current)).filter(Boolean).join(',')
            : preservedState && preservedState.length > 0
              ? Array.from(new Set(preservedState)).filter(Boolean).join(',')
              : '',
          districtId: currentDistrictRef.current && currentDistrictRef.current.length > 0
            ? Array.from(new Set(currentDistrictRef.current)).filter(Boolean).join(',')
            : preservedDistrict && preservedDistrict.length > 0
              ? Array.from(new Set(preservedDistrict)).filter(Boolean).join(',')
              : '',
          searchTerm: searchValue !== undefined ? searchValue : effectiveSearchValue,
          // Handle selective price range - only include entered values
          ...(overridePriceRange ?
            (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange) ?
              overridePriceRange : // Use selective price object directly
              {
                minPrice: overridePriceRange[0],
                maxPrice: overridePriceRange[1],
              }
            ) : filtersRef.current.priceRange ? {
              minPrice: filtersRef.current.priceRange[0],
              maxPrice: filtersRef.current.priceRange[1],
            } : {}),
        };
        console.warn('Common variables for API call:', commonVariables);

        // Log the price values being sent to API
        if (overridePriceRange) {
          if (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange)) {
          } else {
            console.info('🎯 API call using overridePriceRange array:', overridePriceRange);
          }
        } else if (filtersRef.current.priceRange) {
          console.info('📊 API call using filtersRef priceRange:', filtersRef.current.priceRange);
        } else {
          console.info('❌ API call with no price range');
        }

        // OPTIMIZED: Single API call for current billboard type only
        const effectiveBillboardType = overrideBillboardType || billboardType;
        // Build variables for API call
        const gqlVariables: any = {
          ...commonVariables,
          page: effectivePage,
          size: dataListLength,
          billboard: effectiveBillboardType,
          filterData: { ...filter, type: effectiveBillboardType.toUpperCase() },
          isCurated: true,
        };
        if (effectiveBillboardType.toLowerCase() === 'digital') {
          gqlVariables.spotBasedBookingOnly = spotBasedBooking;
        }

        const response = await client.query({
          query: GETPROPERTY_LIST,
          variables: gqlVariables,
          fetchPolicy: 'no-cache', // Always fetch fresh data
        });

        const currentBillboards = response.data.landingpageExploreProperties || [];

        // Update count for current billboard type only
        const currentTypeTotal = currentBillboards[0]?.totalSize || 0;
        if (effectiveBillboardType.toLowerCase() === 'classic') {
          setclasicTotalSize(currentTypeTotal);
          // Keep existing digital count, don't fetch it
        } else {
          setdigitalTotalSize(currentTypeTotal);
          // Keep existing classic count, don't fetch it
        }

        // Update billboard data with cart status
        const billboardsWithCartState = currentBillboards.map((billboard: any) => ({
          ...billboard,
          isInCart: cartBillboardIds.has(billboard.id),
          nextAvailableDate: billboard.nextAvailableDate || format(new Date(), 'yyyy-MM-dd'),
        }));

        setBillBoardData(billboardsWithCartState);
        setTotalDataLength(currentBillboards[0]?.totalSize || 0);

        // Update pagination data
        if (currentBillboards.length > 0) {
          const totalSize = currentBillboards[0].totalSize;
          if (effectivePage === 0) {
            setPageStartData(1);
            setPageEndData(Math.min(dataListLength, totalSize));
          } else {
            const curStartData = dataListLength * effectivePage;
            setPageStartData(curStartData + 1);
            setPageEndData(Math.min(curStartData + dataListLength, totalSize));
          }
        } else {
          setPageStartData(0);
          setPageEndData(0);
          setNoDataMessage(
            district
              ? `No ${billboardType} inventories found for the selected district.`
              : state
                ? `No ${billboardType} inventories found for the selected state.`
                : `No ${billboardType} inventories found for your filters.`
          );
        }

        // Update filteredBillboards with the top 5 from the current billboards
        const topBillboards = currentBillboards
          .slice(0, 5)
          .map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
            nextAvailableDate: billboard.nextAvailableDate || format(new Date(), 'yyyy-MM-dd'),
          }));
        setFilteredBillboards(topBillboards);

        if (viewMode === 'map' && map) {
          setTimeout(() => {
            if (map) {
              map.resize();
              map.flyTo({
                center: [153.0251, -27.4698],
                zoom: 5,
                essential: true,
              });
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error in fetchAllBillboardData:", error);
      setNoDataMessage(
        district
          ? `No ${billboardType} inventories found for the selected district.`
          : state
            ? `No ${billboardType} inventories found for the selected state.`
            : `No ${billboardType} inventories found for your filters.`
      );
      setBillBoardData([]);
      setFilteredBillboards([]);
      setTotalDataLength(0);
      setPageStartData(0);
      setPageEndData(0);
      toast({
        title: "Error",
        description: "Failed to load billboard data.",
        variant: "destructive"
      });
    } finally {
      setBillBoardDataLoading(false);
    }
  };

  // Function to fetch packages using PACKAGE_BILLBOARD query
  const fetchPackageData = async (cartPackageIds = new Set<string>(), countryId?: string, searchValue?: string, page?: number) => {
    const effectiveCountryId = getEffectiveCountryId(countryId);
    if (!effectiveCountryId || countryOptions.length === 0) {
      console.info('🚫 Skipping package API call: no valid country');
      return;
    }

    const effectivePage = page !== undefined ? page : currentPage;

    // ✅ Deduplication: Skip if same package fetch already requested recently (within last 1 second)
    const now = Date.now();
    if (
      lastPackageFetchRef.current.countryId === effectiveCountryId &&
      lastPackageFetchRef.current.page === effectivePage &&
      lastPackageFetchRef.current.timestamp &&
      now - lastPackageFetchRef.current.timestamp < 1000 // Within 1 second
    ) {
      console.info('⏭️ Skipping duplicate package fetch - same params within 1 second:', {
        countryId: effectiveCountryId,
        page: effectivePage
      });
      return;
    }

    // Update the last fetch ref
    lastPackageFetchRef.current = {
      countryId: effectiveCountryId,
      page: effectivePage,
      timestamp: now
    };

    // Prevent multiple simultaneous calls
    if (packageDataLoading) {
      return;
    }

    const stateIdString = Array.from(new Set(state)).filter(Boolean).join(',');
    if (!stateIdString && district.length > 0) {
      setNoDataMessage('Please select at least one state to load packages.');
      return;
    }

    try {
      setPackageDataLoading(true);
      setNoDataMessage(null);

      const effectiveStartDate = startDate;
      const effectiveEndDate = endDate;

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // For mock data, use generateMockData
        try {
          const mockResponse = await generateMockData(PACKAGE_BILLBOARD);

          if (!mockResponse || !mockResponse.data) {
            console.error("Mock package data response is null or missing data property");
            setNoDataMessage('Mock package data failed to load.');
            setPackageDataLoading(false);
            return;
          }

          const packages = mockResponse.data.packageBillboard || [];
          
          // Apply search filter to mock data if search value is provided
          let filteredPackages = packages;
          const effectiveSearchValue = searchValue !== undefined ? searchValue : search;
          if (effectiveSearchValue && effectiveSearchValue.trim().length > 0) {
            filteredPackages = packages.filter((pkg: any) => {
              const searchTerm = effectiveSearchValue.toLowerCase();
              return (
                (pkg.displayName && pkg.displayName.toLowerCase().includes(searchTerm)) ||
                (pkg.name && pkg.name.toLowerCase().includes(searchTerm)) ||
                (pkg.description && pkg.description.toLowerCase().includes(searchTerm)) ||
                (pkg.location && pkg.location.toLowerCase().includes(searchTerm))
              );
            });
          }

          if (filteredPackages.length === 0) {
            setNoDataMessage(effectiveSearchValue ? 'No packages found for your search.' : 'No Packages Available.');
            setPackageData([]);
            setPackageDataLoading(false);
            return;
          }

          // Add cart status to packages
          const packagesWithCartState = filteredPackages.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }));

          setPackageData(packagesWithCartState);

        } catch (error) {
          console.error("Error in mock package data processing:", error);
          setNoDataMessage('Failed to load mock package data.');
          setPackageDataLoading(false);
          return;
        }
      } else {
        // Real API call
        try {
          // Use selected media owner ID as companyId from filtersRef (always up-to-date), or empty string if no media owner selected
          const selectedCompanyId = (filtersRef.current.mediaOwner && filtersRef.current.mediaOwner.length > 0)
            ? filtersRef.current.mediaOwner.join(',')
            : "";
          const today = moment().format('YYYY-MM-DD');
          const { data } = await client.query({
            query: PACKAGE_BILLBOARD,
            variables: {
              accessToken: token,
              page: effectivePage,
              size: 10,
              sort: "last_modified_date,desc",
              startDate: safeFormatDate(effectiveStartDate, 'yyyy-MM-dd'),
              endDate: safeFormatDate(effectiveEndDate, 'yyyy-MM-dd'),
              exposure: exposure,
              packageDate: today,
              packageType: "DAYWISE",
              publisherId: selectedCompanyId, // Empty to fetch all publishers
              countryId: countryId || selectedCountryId, // Use passed countryId or fallback to state
              searchTerm: searchValue !== undefined ? searchValue : search,
            },
            fetchPolicy: 'no-cache', // Always fetch fresh data
          });
          const packages = data?.packageBillboard || [];

          if (packages.length === 0) {
            setNoDataMessage('No packages available for the selected criteria.');
            setPackageData([]);
            setPackageTotalSize(0);
            setTotalDataLength(0);
            setPageStartData(0);
            setPageEndData(0);
            return;
          }

          // Add cart status to packages
          const packagesWithCartState = packages.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }));

          setPackageData(packagesWithCartState);
          
          // Extract totalPackages and totalPages from the first package in response
          const totalPackages = packages[0]?.totalPackages;
          const totalPages = packages[0]?.totalPages;
          setPackageTotalSize(totalPackages);
          setTotalDataLength(totalPackages);

          // Update pagination data
          if (effectivePage === 0) {
            setPageStartData(1);
            setPageEndData(Math.min(dataListLength, totalPackages));
          } else {
            const curStartData = dataListLength * effectivePage;
            setPageStartData(curStartData + 1);
            setPageEndData(Math.min(curStartData + dataListLength, totalPackages));
          }

        } catch (error) {
          console.error("Error fetching package data:", error);
          setNoDataMessage('Failed to load package data.');
          setPackageData([]);
          setPackageTotalSize(0);
          setTotalDataLength(0);
          setPageStartData(0);
          setPageEndData(0);
          toast({
            title: "Error",
            description: "Failed to load package data.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error("Error in fetchPackageData:", error);
      setNoDataMessage('Failed to load package data.');
      setPackageData([]);
      toast({
        title: "Error",
        description: "Failed to load package data.",
        variant: "destructive"
      });
    } finally {
      setPackageDataLoading(false);
    }
  };

  // Helper function to handle billboard type change
  const handleBillboardTypeChange = useCallback((newType: string) => {
    if (billboardType !== newType) {
      // Set flag to prevent automatic API calls during manual change
      isManualBillboardTypeChange.current = true;

      // Update both billboardType and filters.category together
      setBillboardType(newType);
      const updatedFilters = {
        category: newType,
        // Clear Environment and Media Owner when switching to packages
        ...(newType === 'packages' && {
          environment: '',
          mediaOwner: []
        })
      };
      setFilters((prev: any) => ({
        ...prev,
        ...updatedFilters
      }));

      // 🔧 FIX: Update filtersRef immediately to reflect cleared mediaOwner
      if (newType === 'packages') {
        filtersRef.current = {
          ...filtersRef.current,
          ...updatedFilters,
          mediaOwner: []
        };
      }

      // Save to localStorage
      localStorage.setItem('proBillboardType', newType);

      // Reset page to first page when switching billboard types
      if (currentPage !== 0) {
        setCurrentPage(0);
      }

      // Clear any no data message
      setNoDataMessage(null);

      // Gather cart IDs needed for isInCart flags
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      if (newType === 'packages') {
        const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));

        fetchPackageData(cartPackageIds, selectedCountryId, search).finally(() => {
          setTimeout(() => {
            isManualBillboardTypeChange.current = false;
          }, 500);
        });
      } else {
        fetchAllBillboardData(search, 0, cartBillboardIds, newType).finally(() => {
          setTimeout(() => {
            isManualBillboardTypeChange.current = false;
          }, 500);
        });
      }
    }
  }, [billboardType, currentPage, search, cartDataList, fetchAllBillboardData, selectedCountryId]);

  // Memoize with stable dependencies
  const fetchAllBillboardDataMemo = useCallback(fetchAllBillboardData, [
    search,
    currentPage,
    startDate,
    endDate,
    billboardType,
    JSON.stringify(locationData),
    JSON.stringify(siteTypeData),
    JSON.stringify(venueTypeData),
    JSON.stringify(filters.mediaOwner || []),
    JSON.stringify(filters.priceRange ?? null),
    token,
    userId,
    selectedCountryId,
    dataListLength,
    state,
    district,
    handleBillboardTypeChange,
  ]);

  const debouncedFetchAllBillboardData = useMemo(
    () => debounce((searchValue: string, page: number, cartBillboardIds: Set<string>, reason: string = 'unknown', stateIds?: string[], districtIds?: string[], countryId?: string) => {
      if (isManualBillboardTypeChange.current || isManualStateChange.current || isManualDistrictChange.current || isManualCountryChange.current) {
        return;
      }
      fetchAllBillboardDataMemo(searchValue, page, cartBillboardIds, undefined, stateIds, undefined, countryId, districtIds);
    }, 300, { leading: false, trailing: true }), // Single debounce for all calls
    [fetchAllBillboardDataMemo, selectedCountryId] // Include selectedCountryId to fix closure issues
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    // Only trigger if not during initial mount
    if (hasInitialized.current) {
      // Don't reset page - keep current page when spotBasedBooking changes
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));
      // Use currentPage to maintain pagination across buying format changes
      fetchAllBillboardData(search, currentPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    } else {
      hasInitialized.current = true;
    }
  }, [spotBasedBooking]);

  const mapInitialized = useRef(false);

  useEffect(() => {

    if (viewMode !== 'map' || !mapContainer.current || mapInitialized.current) return;

    const initializeMap = () => {
      try {
        mapboxgl.accessToken = MAPBOX_TOKEN;

        const newMap = new mapboxgl.Map({
          container: mapContainer.current!,
          style: mapMode === 'map'
            ? 'mapbox://styles/mapbox/streets-v11'
            : 'mapbox://styles/mapbox/satellite-v9',
          center: [153.0251, -27.4698], // Default center
          zoom: 10,
        });

        newMap.addControl(new mapboxgl.NavigationControl());

        // Handle map load event
        newMap.on('load', () => {
          newMap.resize();

          // Create markers after map is loaded if data is available
          if (billBoardData && billBoardData.length > 0) {

            // Clear any existing markers first
            markers.forEach(marker => {
              try {
                marker.remove();
              } catch (error) {
                console.error('Error removing marker:', error);
              }
            });

            // Create and add markers
            const initialMarkers = billBoardData
              .filter((billboard: any) =>
                billboard.latitude != null &&
                billboard.longitude != null &&
                !isNaN(billboard.latitude) &&
                !isNaN(billboard.longitude)
              )
              .map((billboard: any) => {
                try {
                  // Create popup for billboard
                  const popupContent = `
                    <div style="width: 200px; padding: 10px;">
                      <h6>${billboard.displayName || 'Unknown'}</h6>
                      <p class="mb-1"><small>${billboard.stateName || 'N/A'}</small></p>
                      <p class="mb-1"><small>${billboard?.price?.localCurrencies?.code || 'N/A'} ${billboard.type === 'DIGITAL' ? (priceFormat === 'spot-based' ? (billboard.perSpotPrice || 'N/A') + '/Spot' : (billboard.perDayPrice || 'N/A') + '/Day') : ((billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) ? billboard.sellingRate.monthly.month1 : (billboard.currentPrice || 'N/A')) + '/Month'}</small></p>
                      <button class="btn btn-outline-secondary btn-sm w-100 mt-1" onclick="document.getElementById('view-details-${billboard.id}').click()">
                        View Details
                      </button>
                    </div>
                  `;

                  const popup = new mapboxgl.Popup({
                    offset: 25,
                    closeOnClick: false,
                  }).setHTML(popupContent);

                  const marker = new mapboxgl.Marker({
                    color: billboard.availableBooking ? '#28a745' : '#dc3545',
                  })
                    .setLngLat([billboard.longitude, billboard.latitude])
                    .setPopup(popup)
                    .addTo(newMap);

                  return marker;
                } catch (error) {
                  console.error(`Error creating marker for billboard ${billboard.id}:`, error);
                  return null;
                }
              })
              .filter((marker: any): marker is mapboxgl.Marker => marker !== null);

            setMarkers(initialMarkers);

            // Fit bounds to show all markers
            if (initialMarkers.length > 0) {
              try {
                const bounds = new mapboxgl.LngLatBounds();
                let hasValidBounds = false;

                initialMarkers.forEach((marker: any) => {
                  try {
                    const [lng, lat] = marker.getLngLat().toArray();
                    bounds.extend([lng, lat]);
                    hasValidBounds = true;
                  } catch (error) {
                    console.error('Error extending bounds with marker:', error);
                  }
                });

                if (hasValidBounds) {
                  newMap.fitBounds(bounds, {
                    padding: 100,
                    maxZoom: 15,
                  });
                }
              } catch (error) {
                console.error('Error fitting map bounds:', error);
              }
            }
          }
        });

        // Error handling
        newMap.on('error', (e) => {
          console.error('Mapbox error:', e);
        });

        setMap(newMap);
        mapInitialized.current = true;

        return () => {
          // Remove all markers before removing the map
          markers.forEach(marker => {
            try {
              marker.remove();
            } catch (error) {
              console.error('Error removing marker during cleanup:', error);
            }
          });
          setMarkers([]);

          newMap.remove();
          setMap(null);
          mapInitialized.current = false;
        };
      } catch (error) {
        console.error('Error initializing Mapbox:', error);
        setMap(null);
        mapInitialized.current = false;
        return undefined;
      }
    };

    const cleanup = initializeMap();
    return () => {
      if (cleanup) cleanup();
    };
  }, [viewMode, mapMode, billBoardData, loadingCartItemIds]);


  const isFieldEnabled = (key: string, config: ConfigPage[]) => {
    const fieldConfig = config
      .flatMap(page => page.configurations || [])
      .flatMap(cfg => cfg.fields || [])
      .find(field => field.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };

  // Update map markers when billBoardData changes
  useEffect(() => {
    if (!map || viewMode !== 'map') return;
    // Clear existing markers
    markers.forEach(marker => {
      try {
        marker.remove();
      } catch (error) {
        console.error('Error removing marker:', error);
      }
    });
    setMarkers([]);

    if (!billBoardData?.length) {
      // Center map on default location
      try {
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });
        setTimeout(() => {
          if (map) map.resize();
        }, 100);
      } catch (error) {
        console.error("Error resetting map view:", error);
      }
      return;
    }

    // Add new markers
    const newMarkers: mapboxgl.Marker[] = billBoardData
      .filter((billboard: any) =>
        billboard.latitude != null &&
        billboard.longitude != null &&
        !isNaN(billboard.latitude) &&
        !isNaN(billboard.longitude)
      )
      .map((billboard: any) => {
        try {

          const popupContent = `
          <div style="width: 200px; padding: 10px;">
            <h6>${billboard.displayName || 'Unknown'}</h6>
            <p class="mb-1"><small>${billboard.stateName || 'N/A'}</small></p>
            <p class="mb-1"><small>${billboard?.price?.localCurrencies?.code || 'N/A'} ${billboard.type === 'DIGITAL' ? (priceFormat === 'spot-based' ? (billboard.perSpotPrice || 'N/A') + '/Spot' : (billboard.perDayPrice || 'N/A') + '/Day') : ((billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) ? billboard.sellingRate.monthly.month1 : (billboard.currentPrice || 'N/A')) + '/Month'}</small></p>
            <button class="btn btn-outline-secondary btn-sm w-100 mt-1" onclick="document.getElementById('view-details-${billboard.id}').click()">
              View Details
            </button>
          </div>
        `;

          const popup = new mapboxgl.Popup({
            offset: 25,
            closeOnClick: false,
          }).setHTML(popupContent);

          const marker = new mapboxgl.Marker({
            color: billboard.availableBooking ? '#28a745' : '#dc3545',
          })
            .setLngLat([billboard.longitude, billboard.latitude])
            .setPopup(popup);

          // Only add to map if valid
          if (map) {
            marker.addTo(map);
            marker.getElement().addEventListener('click', () => {
              if (map) {
                map.flyTo({
                  center: [billboard.longitude, billboard.latitude],
                  zoom: 14,
                });
                popup.addTo(map);
              }
            });
          }

          return marker;
        } catch (error) {
          console.error(`Error creating marker for billboard ${billboard.id}:`, error);
          return null;
        }
      })
      .filter((marker: any): marker is mapboxgl.Marker => marker !== null);

    setMarkers(newMarkers);

    // Fit map to bounds
    if (newMarkers.length > 0 && map) {
      try {
        const bounds = new mapboxgl.LngLatBounds([0, 0, 0, 0]); // Initialize with dummy coordinates
        let hasValidBounds = false;

        newMarkers.forEach(marker => {
          try {
            const [lng, lat] = marker.getLngLat().toArray();
            bounds.extend([lng, lat]);
            hasValidBounds = true;
          } catch (error) {
            console.error('Error extending bounds with marker:', error);
          }
        });

        if (hasValidBounds) {
          map.fitBounds(bounds as mapboxgl.LngLatBoundsLike, {
            padding: 100,
            maxZoom: 15,
          });
        } else {
          // Fallback if bounds calculation fails
          map.flyTo({
            center: [153.0251, -27.4698],
            zoom: 5,
            essential: true
          });
        }
      } catch (error) {
        console.error('Error fitting map bounds:', error);
        // Fallback if fitting bounds fails
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });
      }
    } else if (map) {
      // Reset view if no markers
      try {
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });
      } catch (error) {
        console.error("Error resetting map view:", error);
      }
    }
  }, [map, billBoardData, loadingCartItemIds, viewMode, billboardType]);

  // Update map style when mapMode changes
  // Update map style when mapMode changes
  useEffect(() => {
    if (map) {
      map.setStyle(
        mapMode === 'map'
          ? 'mapbox://styles/mapbox/streets-v11'
          : 'mapbox://styles/mapbox/satellite-v9'
      );
    }
  }, [map, mapMode]);

  // Make sure map is properly sized when in map view
  useEffect(() => {
    if (!map || viewMode !== 'map') return;

    // Even if the map is already initialized, we want to make sure it's properly sized
    // This helps with scenarios where we switch to map view but have no data
    setTimeout(() => {
      try {
        if (map) {
          map.resize();
        }
      } catch (error) {
        console.error("Error resizing map:", error);
      }
    }, 100);

  }, [map, viewMode, noDataMessage]);

  // Keep track of cart items in localStorage
  useEffect(() => {
    localStorage.setItem('campaignCart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = async (filters: BillboardFilters, shouldApply: boolean = true) => {
    const newState = Array.isArray(filters.state) ? filters.state : [filters.state].filter(Boolean);
    const newDistrict = Array.isArray(filters.district) ? filters.district : [filters.district].filter(Boolean);

    if (JSON.stringify(newState) !== JSON.stringify(state)) {
      setState(newState);
      if (shouldApply) {
        localStorage.setItem('billboardFilters_state', newState.join(','));
      }
    }
    if (JSON.stringify(newDistrict) !== JSON.stringify(district)) {
      setDistrict(newDistrict);
      if (shouldApply) {
        localStorage.setItem('billboardFilters_district', newDistrict.join(','));
      }
    }
    if (shouldApply) {
      localStorage.setItem('billboardFilters_environment', JSON.stringify(filters.environment || []));
      localStorage.setItem('billboardFilters_venueTypes', JSON.stringify(filters.venueTypes || []));
      localStorage.setItem('billboardFilters_siteTypes', JSON.stringify(filters.siteTypes || []));
      localStorage.setItem('billboardFilters_size', filters.size || '');
      localStorage.setItem('billboardFilters_priceRange', JSON.stringify(filters.priceRange || [0, 0]));
      try {
        setBillBoardDataLoading(true);
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        debouncedFetchAllBillboardData(search, 0, cartBillboardIds, 'handleFilterChange', undefined, undefined, selectedCountryId);
      } catch (error) {
        console.error('Error fetching filtered data:', error);
      } finally {
        setBillBoardDataLoading(false);
      }
    }
  };

  // Trigger API call when venueTypeData changes
  useEffect(() => {
    if (venueTypeData && venueTypeData.length > 0) {
      handleFilterChange({ ...filters, venueType: venueTypeData });
    }
  }, [venueTypeData]);

  const addToCart = async (items: any[] | any) => {
    // Skip if already processing a cart request
    if (cartRequestInProgress.current) {
      return;
    }

    const itemsArray = Array.isArray(items) ? items : [items];

    // Get the IDs of the items to be added
    const itemIds = itemsArray.map(item => item.id || item.inventoryId || item.cartItemId);

    // Check if any of these items are already in the cart
    const existingItemIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Filter out items that are already in the cart or are currently loading
    const itemsToAdd = itemsArray.filter(item => {
      const itemId = item.id || item.inventoryId || item.cartItemId;
      return !existingItemIds.has(itemId) && !loadingCartItemIds.has(itemId);
    });

    // If all items are already in the cart, return early
    if (itemsToAdd.length === 0) {
      if (itemsArray.length === 1 && existingItemIds.has(itemsArray[0].id)) {
        toast({
          title: "Info",
          description: `${itemsArray[0].displayName || 'Item'} is already in your cart.`,
          variant: "info"
        });
      }
      return;
    }

    // Get IDs of items that will be added (not already in cart)
    const newItemIds = itemsToAdd.map(item => item.id || item.inventoryId || item.cartItemId);
    setLoadingCartItemIds(prev => new Set([...prev, ...newItemIds]));

    // Optimistically update isInCart for billboards
    setBillBoardData((prevData: any[]) =>
      prevData.map(billboard =>
        newItemIds.includes(billboard.id) ? { ...billboard, isInCart: true } : billboard
      )
    );
    setFilteredBillboards((prev: any[]) =>
      prev.map(billboard =>
        newItemIds.includes(billboard.id) ? { ...billboard, isInCart: true } : billboard
      )
    );

    // Optimistically update isInCart for packages
    setPackageData((prevData: any[]) =>
      prevData.map(pkg =>
        newItemIds.includes(pkg.id) ? { ...pkg, isInCart: true } : pkg
      )
    );

    // Optimistically update cart count
    const previousCartCount = cartCount;
    setCartCount(prev => prev + itemsToAdd.length);
    setCartItemCountContext(cartCount + itemsToAdd.length); // Sync with cart context optimistically

    if (!startDate || !endDate) {
      console.error('Campaign dates are not set. Please edit the campaign.');
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setCartCount(previousCartCount); // Revert cart count
      return;
    }

    const formattedItems = itemsArray.map((item) => ({
      accessToken: token,
      userId: userId || item.userId,
      campaignName: campaignName,
      startDate: safeFormatDate(startDate, 'dd MMMM yyyy'),
      endDate: safeFormatDate(endDate, 'dd MMMM yyyy'),
      cartItemName: item.displayName || item.inventoryName || item.cartItemName,
      cartItemId: item.id || item.inventoryId || item.cartItemId,
      cartItemReferenceId: item.referenceId || item.inventoryReferenceId || item.cartItemReferenceId,
      cartItemType: item.type || item.inventoryType || item.cartItemType,
      cartItemCountry: item.countryName || item.cartItemCountry,
      cartItemVenueType: item.venueType || item.inventoryVenueType || item.cartItemVenueType || "",
      cartItemResolution:
        item.type === 'CLASSIC'
          ? `${item.panelHeight || 0}x${item.panelWidth || 0}`
          : `${item.resolutionHeight || 1080}x${item.resolutionWidth}` || item.cartItemResolution,
      cartItemThumbnailUrl:
        item.fullResImagePath && item.fullResImagePath !== "null"
          ? item.fullResImagePath
          : item.inventoryThumbnailUrl || item.cartItemThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
      cartItemLatitude: parseFloat(item.latitude || item.inventoryLatitude || item.cartItemLatitude),
      cartItemLongitude: parseFloat(item.longitude || item.inventoryLongitude || item.cartItemLongitude),
      packageDetails: item.packageDetails ?? "",
      isSpotBased: (item.type?.toLowerCase() === 'digital') ? spotBasedBooking : false,
    }));

    try {
      const { data, errors } = await addtoCartQuery({
        variables: { input: formattedItems },
      });

      if (data) {
        // Also add items to cart context for real-time updates
        itemsToAdd.forEach(item => {
          const cartItem = {
            id: (item.id || item.inventoryId || item.cartItemId).toString(),
            name: item.displayName || item.inventoryName || item.name || "Billboard",
            location: item.formattedAddress || `${item.trDistrictName}, ${item.trStateName}` || item.location || "Unknown",
            state: item.trStateName || item.state || "Unknown",
            price: (item.currentPrice || item.sellingRate?.monthly?.month1 || item.price || 0) * 100, // Convert to cents for context
            impressions: item.impressionPerSpot || item.monthlySummary?.totalVisitors || item.impressions || 0,
            uniqueVisitors: item.monthlySummary?.uniqueVisitors || 0,
            frequency: item.monthlySummary?.frequency || 0,
            size: item.type === 'CLASSIC'
              ? (item.panelWidth && item.panelHeight
                ? `${item.panelWidth}x${item.panelHeight}`
                : 'Standard')
              : (item.resolutionWidth && item.resolutionHeight
                ? `${item.resolutionWidth}x${item.resolutionHeight}`
                : 'Standard'),
            type: item.type === 'CLASSIC' ? 'Classic' : item.type === 'DIGITAL' ? 'Digital' : 'Billboard',
            status: item.availableBooking ? "available" as const : "sold-out" as const,
            image: item.fullResImagePath || item.thumbnailPath || item.imageUrl || item.image || "",
            specifications: {
              resolution: item.type === 'CLASSIC'
                ? (item.panelWidth && item.panelHeight
                  ? `${item.panelWidth}x${item.panelHeight}`
                  : 'Standard')
                : (item.resolutionWidth && item.resolutionHeight
                  ? `${item.resolutionWidth}x${item.resolutionHeight}`
                  : item.specification?.resolution1Width && item.specification?.resolution1Height
                    ? `${item.specification.resolution1Width}x${item.specification.resolution1Height}`
                    : "1920x1080"),
              duration: item.type === 'DIGITAL' ? item?.price?.spotDuration : "Monthly"
            },
            priceFormat,
          };
          addToCartContext(cartItem);
        });

        // Only call fetchCartDetails once - it will update cart count and trigger cartUpdated event
        // Don't call fetchCartCountDetails separately as it's redundant
        await fetchCartDetails();

        // setShowCart(true);
        // window.dispatchEvent(new Event('cartUpdated'));
        toast({
          title: "Success",
          description: "Billboard has been added to your cart successfully.",
          variant: "info"
        })
        // toast.success(`${formattedItems.length} item(s) added to cart successfully!`);
      } else if (errors && errors.length > 0) {
        // Revert optimistic updates
        setBillBoardData((prevData: any[]) =>
          prevData.map(billboard =>
            itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
          )
        );
        setFilteredBillboards((prev: any[]) =>
          prev.map(billboard =>
            itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
          )
        );
        setPackageData((prevData: any[]) =>
          prevData.map(pkg =>
            itemIds.includes(pkg.id) ? { ...pkg, isInCart: false } : pkg
          )
        );
        setCartCount(previousCartCount); // Revert cart count
        setCartItemCountContext(previousCartCount); // Sync with cart context
        toast({
          title: "Error",
          description: errors[0].message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      // Revert optimistic updates
      setBillBoardData((prevData: any[]) =>
        prevData.map(billboard =>
          itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
        )
      );
      setFilteredBillboards((prev: any[]) =>
        prev.map(billboard =>
          itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
        )
      );
      setPackageData((prevData: any[]) =>
        prevData.map(pkg =>
          itemIds.includes(pkg.id) ? { ...pkg, isInCart: false } : pkg
        )
      );
      setCartCount(previousCartCount); // Revert cart count
      setCartItemCountContext(previousCartCount); // Sync with cart context
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      // Remove item IDs from the loading set
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      // Always reset the in-progress flag
      cartRequestInProgress.current = false;
    }
  };

  // Using refs to track if a request is in progress - prevent duplicate API calls
  const cartRequestInProgress = useRef(false);
  const cartCountRequestInProgress = useRef(false);
  const cartDetailsRequestInProgress = useRef(false);
  const lastCartUpdateTime = useRef<number>(0);

  // Function to fetch invoice details for a campaign
  const fetchInvoiceDetails = async (campaignId: string) => {
    if (!campaignId) {
      console.warn("Cannot fetch invoice: No campaign ID provided");
      return null;
    }

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockdata = await generateMockData(INVOICE);
        return mockdata?.data.paymentInvoice;
      } else {
        const { data } = await client.query({
          query: INVOICE,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            campId: campaignId,
          },
        });
        return data?.paymentInvoice;
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      return null;
    }
  };

  const fetchCartDetails = async () => {
    // If a request is already in progress, skip this one
    if (cartRequestInProgress.current) {
      // Skip duplicate cart fetch requests
      return;
    }

    cartRequestInProgress.current = true;
    setIsCartLoading(true);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockdata = await generateMockData(CART_PAGE_ITEM);
        const campaignData = mockdata.data.campaign || {};
        setCartData(mockdata.data.cartPageItem || []);
        setCartDataList(mockdata.data.cartPageItem || []);
        setCartItems(mockdata.data.cartPageItem || []);
        const mockCount = mockdata.data.cartPageItem?.length || 0;
        setCartCount(mockCount);
        setCartItemCountContext(mockCount); // Sync with cart context
        // // Mock cart data
        // const mockCartData = [{
        //   id: 'mock-cart-id',
        //   cartItemId: 'mock-cart-item-id',
        //   campaignInventories: [],
        //   campaignId: 'mock-campaign-id',
        //   campaignName: campaignName || 'Mock Campaign'
        // }] as CampaignInventory[];

        // // Convert mockCartData to CartItem format
        // const mockCartItems: CartItem[] = mockCartData.map(item => {
        //   const { cartItemId, ...rest } = item;
        //   return {
        //     billboardId: item.id || '',
        //     cartItemId: cartItemId || '',
        //         dateRange: {
        //       start: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        //       end: endDate ? format(endDate, 'yyyy-MM-dd') : ''
        //     },
        //     campaignInventories: item.campaignInventories || [],
        //     overAlltotalPrice: 0,
        //     ...rest
        //   };
        // });

        // setCartDataList(mockCartData);
        // setCartData(mockCartData);
        // setCartItems(mockCartItems);
        // setCartCount(mockCartData.length);
      } else {
        // Don't mark existing cart items as loading when fetching cart details
        // We only want to track new items being added as loading

        // Ensure startDate and endDate are not null since they're non-nullable in the GraphQL schema
        if (!startDate || !endDate) {
          console.error('Start date or end date is missing for cart fetch');
          toast({
            title: "Error",
            description: "Campaign dates are required to fetch cart details",
            variant: "destructive"
          });
          cartRequestInProgress.current = false;
          setIsCartLoading(false);
          return;
        }

        const { data } = await client.query({
          query: CART_PAGE_ITEM,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            campaignId: campaignId || '',
            userId: userId,
            edit: edit,
            startDate: safeFormatDate(startDate, 'yyyy-MM-dd', 'yyyy-MM-dd'),
            endDate: safeFormatDate(endDate, 'yyyy-MM-dd', 'yyyy-MM-dd'),
            exposure: exposure
          },
        });

        const fetchedCartDataList = data.cartPageItem || [];

        // If no cart data is returned, ensure cart count is 0
        if (fetchedCartDataList.length === 0) {
          setCartCount(0);
          setCartItemCountContext(0); // Sync with cart context
          setCartDataList([]);
          setCartData([]);
          setCartItems([]);
          return;
        }

        // Create separate sets for different item types from cart response
        const cartBillboardIds = new Set(
          fetchedCartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType !== 'package') // Regular billboards (digital/classic)
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        const cartPackageIds = new Set(
          fetchedCartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package') // Package items  
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        // Update billboard data with cart status - Important for keeping consistent cart state
        setBillBoardData((prevData: any) =>
          prevData.map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }))
        );

        // Update filtered billboards with cart status
        setFilteredBillboards((prev: any[]) =>
          prev.map((billboard) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }))
        );

        // Update package data with cart status
        setPackageData((prevData: any[]) =>
          prevData.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }))
        );

        // Add state information to each billboard in the cart
        const enhancedCartData = fetchedCartDataList.map((cartItem: any) => {
          if (cartItem.campaignInventories && cartItem.campaignInventories.length > 0) {
            return {
              ...cartItem,
              campaignInventories: cartItem.campaignInventories.map((inventory: any) => {
                // Find the corresponding billboard to get its state
                const matchingBillboard = billBoardData.find((bb: any) => bb.id === inventory.billboardId);
                return {
                  ...inventory,
                  billboardState: matchingBillboard?.stateName || state || '',
                };
              }),
            };
          }
          return cartItem;
        });

        setCartDataList(enhancedCartData);
        setCartData(enhancedCartData);
        setCartItems(enhancedCartData);
        setCartDataLoaded(true); // Mark cart data as loaded

        // Update cart count based on the number of items in the cart
        const totalItems = fetchedCartDataList.reduce((count: number, cart: any) => {
          return count + (cart.campaignInventories?.length || 0);
        }, 0);
        setCartCount(totalItems);
        setCartItemCountContext(totalItems); // Sync with cart context

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error fetching cart details:", error);
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        console.info("Using mock data, ignoring API error");
      } else {
        // Provide more specific error messages based on the error type

        if (error instanceof Error) {
          if (error.message.includes('startDate') || error.message.includes('endDate')) {
            toast({
              title: "Error",
              description: "Campaign dates are required to fetch cart details",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Error",
              description: `Failed to fetch cart details: ${error.message}`,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch cart details. Please try again.",
            variant: "destructive"
          });
        }
      }
    } finally {
      setIsCartLoading(false);
      cartRequestInProgress.current = false;
      // We intentionally don't reset loadingCartItemIds here
      // as that would mark all cart items as "loading"
    }
  };

  // Add useEffect to initialize cart state on component mount with better safeguards
  useEffect(() => {
    // Check if the component just mounted or if dates changed
    if (!initialCartLoadDone.current) {

      // Fetch cart details with valid dates
      fetchCartDetails();
      fetchCartCountDetails();

      // Mark initial cart load as done
      initialCartLoadDone.current = true;
      cartInitializationRef.current = true;
    }
  }, []); // Removed startDate and endDate dependencies to prevent refetching when dates change

  // Consolidate Cart Fetching Logic
  useEffect(() => {
    const loadCartDetails = async () => {
      // Guard: If in edit mode, we must wait for the campaignId to be parsed from URL
      if (edit && !campaignId) {
        return;
      }

      // Guard: Prevent duplicate calls if already loaded this session
      if (initialCartLoadDone.current) {
        return;
      }

      try {
        // Run both fetches in parallel
        await Promise.all([
          fetchCartDetails(),
          fetchCartCountDetails()
        ]);

        // Lock future fetches
        initialCartLoadDone.current = true;
        cartInitializationRef.current = true;
      } catch (e) {
        console.error("Error during initial cart load:", e);
      }
    };

    loadCartDetails();
  }, [campaignId, edit]); // Only re-run if campaignId or edit mode changes
  // This effect is for fetching billboard data when filters change
  useEffect(() => {
    const initializeData = async () => {
      if (!startDate || !endDate || !cartDataLoaded || !selectedCountryId || countryOptions.length === 0) {
        console.warn('Required data not ready:', {
          startDate: !!startDate,
          endDate: !!endDate,
          cartDataLoaded,
          selectedCountryId,
          countryOptionsLoaded: countryOptions.length > 0
        });
        return;
      }

      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );

      // API call is handled by other useEffects and handlers
    };

    // Only run this effect if we have all required data including country options
    if (startDate && endDate && cartDataLoaded && selectedCountryId && countryOptions.length > 0) {
      initializeData();
    }
  }, [fetchAllBillboardDataMemo, search, currentPage, cartDataLoaded, selectedCountryId, countryOptions.length]);

  // Listen for cart updated events from other components
  useEffect(() => {
    const handleCartUpdated = () => {
      // Prevent multiple rapid calls to fetchCartDetails
      const now = Date.now();
      if (now - lastCartUpdateTime.current < 1000) {
        return;
      }
      lastCartUpdateTime.current = now;

      if (startDate && endDate && !cartDetailsRequestInProgress.current) {
        cartDetailsRequestInProgress.current = true;
        fetchCartDetails().finally(() => {
          cartDetailsRequestInProgress.current = false;
        });
      }
    };

    // Add event listener for cart updates
    window.addEventListener('cartUpdated', handleCartUpdated);

    // Clean up on unmount
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdated);
    };
  }, []); // Removed startDate and endDate dependencies to prevent refetching when dates change

  // Sync cart context with API cart count
  useEffect(() => {
    if (typeof cartCount === 'number') {
      setCartItemCountContext(cartCount);
    }
  }, [cartCount, setCartItemCountContext]);


  const deleteCartItem = async (itemData: any) => {

    // Get the billboard ID from either source (property list or cart)
    const billboardId = itemData.inventoryId || itemData.id;
    const trackingId = billboardId; // Use the same ID for tracking loading state

    // Double check for valid item data
    if (!itemData || !billboardId) {
      console.error('Invalid item data:', itemData);
      toast({
        title: "Error",
        description: "Invalid item data",
        variant: "destructive"
      });
      return;
    }

    // Find matching item in cart
    const matchingItem = cartDataList.find((list: any) => {
      return list.campaignInventories?.some((item: any) => item.billboardId === billboardId);
    });

    if (!matchingItem) {
      console.error('Item not found in cart:', billboardId);
      toast({
        title: "Error",
        description: "Item not found in cart",
        variant: "destructive"
      });
      return;
    }

    // Find the specific inventory item
    const matchedInventory = matchingItem.campaignInventories?.find((item: any) => item.billboardId === billboardId);
    const cartItemId = matchedInventory?.cartItemId;

    if (!cartItemId) {
      console.error('Cart item ID not found for:', billboardId);
      toast({
        title: "Error",
        description: "Cart item ID not found",
        variant: "destructive"
      });
      return;
    }

    // Check if item is already being processed
    if (loadingCartItemIds.has(trackingId)) {
      return;
    }

    // Determine item type
    const itemType = (itemData.isDraft || matchedInventory.isDraft) ? 'draft-data' : 'cart-data';

    // Save previous states for rollback if needed
    const previousBillboardData = billBoardData;
    const previousFilteredBillboards = filteredBillboards;
    const previousCartCount = cartCount;
    const previousCartDataList = cartDataList;

    try {
      // Add to loading set using the tracking ID
      setLoadingCartItemIds(prev => new Set([...prev, trackingId]));

      // Optimistically update UI states using billboardId for consistency
      setBillBoardData((prevData: any[]) =>
        prevData.map(billboard => ({
          ...billboard,
          isInCart: billboard.id === billboardId ? false : billboard.isInCart
        }))
      );

      setFilteredBillboards((prev: any[]) =>
        prev.map(billboard => ({
          ...billboard,
          isInCart: billboard.id === billboardId ? false : billboard.isInCart
        }))
      );

      // Optimistically update package states as well
      setPackageData((prevData: any[]) =>
        prevData.map(pkg => ({
          ...pkg,
          isInCart: pkg.id === billboardId ? false : pkg.isInCart
        }))
      );

      // Update cart count
      setCartCount(prev => Math.max(0, prev - 1));
      setCartItemCountContext(Math.max(0, cartCount - 1)); // Sync with cart context optimistically

      // Call deletion mutation
      const { data, errors } = await deleteCartQuery({
        variables: {
          accessToken: token,
          deleteItems: [{
            id: cartItemId,
            itemType: itemType
          }]
        },
      });

      // Handle successful deletion
      if (data?.deleteCartItem?.success) {
        // Also remove from cart context for real-time updates
        removeFromCartContext(billboardId.toString());

        toast({
          title: "Success",
          description: data.deleteCartItem.message || "Item removed from cart successfully",
          variant: "default"
        });
        // Remove item from cartDataList without mutating the original
        const updatedCartDataList = cartDataList.map((cart: any) => ({
          ...cart,
          campaignInventories: cart.campaignInventories?.filter(
            (inv: any) => inv.cartItemId !== cartItemId
          ) || []
        })).filter((cart: any) => cart.campaignInventories.length > 0);

        // Update cart data list first
        setCartDataList(updatedCartDataList);
        setCartData(updatedCartDataList);
        setCartItems(updatedCartDataList);

        // Fetch latest cart data only if there are still items - avoid Promise.all to prevent concurrent calls
        if (updatedCartDataList.length > 0) {
          // Only call fetchCartDetails which will update cart count internally
          await fetchCartDetails();
        } else {
          // If cart is now empty, update relevant states
          setCartCount(0);
          setCartItemCountContext(0);
        }

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
        toast({
          title: "Success",
          description: data.deleteCartItem.message || "Item removed from cart successfully",
          variant: "default"
        });
      } else {
        // Handle failure
        const errorMessage = errors?.[0]?.message || data?.deleteCartItem?.message || "Failed to delete item";
        console.error('Delete failed:', errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });

        // Revert all state changes
        setBillBoardData(previousBillboardData);
        setFilteredBillboards(previousFilteredBillboards);
        setCartCount(previousCartCount);
        setCartItemCountContext(previousCartCount); // Sync with cart context
        setCartDataList(previousCartDataList);
      }
    } catch (error) {
      console.error("Error in deleteCartItem:", error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive"
      });

      // Revert all state changes
      setBillBoardData(previousBillboardData);
      setFilteredBillboards(previousFilteredBillboards);
      setCartCount(previousCartCount);
      setCartItemCountContext(previousCartCount); // Sync with cart context
      setCartDataList(previousCartDataList);
    } finally {
      // Clean up loading state using the tracking ID
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackingId);
        return newSet;
      });
    }
  };


  // const handleRemoveFromCart = (billboardId: string) => {
  //   setCartItems(cartItems.filter(item => item.billboardId !== billboardId));
  // };

  // const handleCategoryChange = (category: string) => {
  //   setSelectedCategory(category);
  //   setShowCategoryDropdown(false);
  // };

  const toggleViewMode = useCallback(
    debounce((mode: 'grid' | 'map') => {
      setViewMode(mode);

      // If switching to map mode, make sure map will be visible even with no data
      if (mode === 'map' && map) {
        setTimeout(() => {
          if (map) {
            map.resize();

            // Reset view to default
            map.flyTo({
              center: [153.0251, -27.4698],
              zoom: 5,
              essential: true
            });
          }
        }, 200);
      }
    }, 300),
    [map]
  );


  const paginationPlus = useCallback(() => {
    const totalPages = Math.ceil(totalDataLength / dataListLength);
    if (currentPage >= totalPages - 1) {
      toast({
        title: "Error",
        description: "You are already on the last page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    localStorage.setItem('propcurrentPage', newPage.toString());

    // Get cart billboard/package IDs based on category
    if (filters.category === 'packages') {
      const cartPackageIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      fetchPackageData(cartPackageIds, selectedCountryId, search, newPage);
    } else {
      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      // Explicitly pass selectedCountryId to ensure country is available
      fetchAllBillboardData(search, newPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    }
    scrollToTop();
  }, [totalDataLength, dataListLength, currentPage, search, selectedCountryId, cartDataList, filters.category, fetchAllBillboardData, fetchPackageData]);

  const paginationMinus = useCallback(() => {
    if (currentPage === 0) {
      toast({
        title: "Error",
        description: "You are already on the first page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    localStorage.setItem('propcurrentPage', newPage.toString());

    // Get cart billboard/package IDs based on category
    if (filters.category === 'packages') {
      const cartPackageIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      fetchPackageData(cartPackageIds, selectedCountryId, search, newPage);
    } else {
      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      // Explicitly pass selectedCountryId to ensure country is available
      fetchAllBillboardData(search, newPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    }
    scrollToTop();
  }, [currentPage, search, selectedCountryId, cartDataList, filters.category, fetchAllBillboardData, fetchPackageData]);

  // Update the getPageNumbers function to use proper total count
  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalDataLength / dataListLength);
    const maxPagesToShow = 5;
    const pages = [];
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    startPage = Math.max(0, endPage - maxPagesToShow + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const pageNumbers: (number | string)[] = [];
    if (startPage > 0) {
      pageNumbers.push(0);
      if (startPage > 1) {
        pageNumbers.push('...');
      }
    }
    pageNumbers.push(...pages);
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages - 1);
    }

    return pageNumbers;
  };

  const handlePageClick = (page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    localStorage.setItem('propcurrentPage', page.toString());

    // Get cart billboard/package IDs based on category
    if (filters.category === 'packages') {
      const cartPackageIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      fetchPackageData(cartPackageIds, selectedCountryId, search, page);
    } else {
      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      // Explicitly pass selectedCountryId to ensure country is available
      fetchAllBillboardData(search, page, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    }
    scrollToTop();
  };

  // Handle country selection (single-select)
  const handleCountrySelection = (countryId: string) => {
    // ✅ SCENARIO 4: Check if cart has items - if yes, prevent country change (cart always uses cart country)
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    if (cartHasItems) {
      toast({
        title: "Cannot change country",
        description: `You have already selected inventory from ${countryOptions.find((c: any) => c.value === filters.country)?.label || 'current country'}. To change countries, please remove all selected inventory or start a new proposal.`,
        variant: "destructive"
      });
      setShowCountryDropdown(false);
      return;
    }

    // Check if in draft/edit mode - prevent country change
    if (edit) {
      toast({
        title: "Cannot change country",
        description: "You cannot change the country for a draft campaign. The country is fixed based on the draft.",
        variant: "destructive"
      });
      setShowCountryDropdown(false);
      return;
    }

    // Set flag to prevent automatic API calls during manual country change
    isManualCountryChange.current = true;
    currentStateRef.current = []; // Reset current state ref on country change
    currentDistrictRef.current = []; // Reset current district ref on country change

    // Find the selected country to get its countryId for localStorage
    const selectedCountry = countryOptions.find((c: any) => c.value === countryId || c.id === countryId);

    if (selectedCountry) {
      if (selectedCountry.id === selectedCountryId) {
        setShowCountryDropdown(false);
        return;
      }

      // Set selectedCountryId directly
      setSelectedCountryId(selectedCountry.value);

      setFilters((prev: any) => ({
        ...prev,
        country: selectedCountry.value,
        state: [],
        district: [],
        mediaOwner: []
      }));

      localStorage.setItem('selectedCountryId', selectedCountry.id);
      localStorage.setItem('userChangedCountry', 'true');
      localStorage.setItem('navigationCountryBackup', JSON.stringify({
        countryId: selectedCountry.id,
        countryValue: selectedCountry.value,
        countryLabel: selectedCountry.label,
        timestamp: Date.now(),
        isManualSelection: true
      }));
      countryInitialized.current = true;

      // Update tax information in cart context
      if (selectedCountry.tax) {
        const taxLabel = selectedCountry.tax.label || 'Tax';
        const taxPercent = selectedCountry.tax.percent || 0;
        updateTaxInfo(taxLabel, taxPercent);
      } else {
        updateTaxInfo('Tax', 0);
      }

      // Reset to first page
      setCurrentPage(0);
      localStorage.setItem('propcurrentPage', '0');

      setState([]);
      setDistrict([]);
      setAvailableStates([]);
      setAvailableDistricts([]);
      localStorage.removeItem('campaignListState');

      filtersRef.current = {
        ...filtersRef.current,
        country: selectedCountry.value,
        state: "",
        district: "",
        mediaOwner: [],
        venueType: []
      };

      setFilters((prev: any) => ({
        ...prev,
        country: selectedCountry.value,
        state: [],
        district: [],
        mediaOwner: [],
        venueType: "" // Clear venue type in UI
      }));

      // Call API directly based on category
      if (filters.category === 'packages') {
        const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        fetchPackageData(cartPackageIds, selectedCountry.value, search);
      } else {
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        fetchAllBillboardData(search, 0, cartBillboardIds, undefined, [], undefined, selectedCountry.id, [], "", []);
      }
    }

    setShowCountryDropdown(false);

    // Reset the manual change flag after API calls complete
    setTimeout(() => {
      isManualCountryChange.current = false;
    }, 500);
  };


  useEffect(() => {
    // Run only if not initialized
    if (hasInitialized.current) return;

    const savedListState = localStorage.getItem('campaignListState');
    if (savedListState) {
      try {
        const {
          search: savedSearch,
          currentPage: savedPage,
          state: savedState,
          district: savedDistrict,
          billboardType: savedBillboardType,
          locationData: savedLocationData,
          siteTypeData: savedSiteTypeData,
          venueTypeData: savedVenueTypeData,
          category: savedCategory,
          mediaOwner: savedMediaOwner,
          environment: savedEnvironment,
        } = JSON.parse(savedListState);

        // Update state only if values are different to avoid unnecessary renders
        if (savedSearch !== undefined && savedSearch !== search) setSearch(savedSearch);
        if (savedPage !== undefined && savedPage !== currentPage) setCurrentPage(savedPage);
        if (savedState !== undefined && savedState !== state) setState(savedState);
        if (savedDistrict !== undefined && savedDistrict !== district) setDistrict(savedDistrict);

        if (savedBillboardType !== billboardType) {
          setBillboardType(savedBillboardType);
        }

        if (savedLocationData !== undefined && JSON.stringify(savedLocationData) !== JSON.stringify(locationData))
          setLocationData(savedLocationData);
        if (savedSiteTypeData !== undefined && JSON.stringify(savedSiteTypeData) !== JSON.stringify(siteTypeData))
          setSitetypeData(savedSiteTypeData);
        if (savedVenueTypeData !== undefined && JSON.stringify(savedVenueTypeData) !== JSON.stringify(venueTypeData))
          setVenueTypeData(savedVenueTypeData);

        // ✅ Restore additional filters from campaignListState
        if (savedCategory !== undefined || savedMediaOwner || savedEnvironment) {
          setFilters((prev: any) => ({
            ...prev,
            category: savedCategory || prev.category,
            mediaOwner: savedMediaOwner || prev.mediaOwner || [],
            environment: savedEnvironment || prev.environment || '',
          }));
        }
      } catch (e) {
        console.error('Error parsing campaignListState:', e);
      }
    } else {
      // Set default billboardType if no saved state - digital as default
      if (billboardType !== 'digital') {
        setBillboardType('digital');
      }
    }

    // Mark as initialized
    hasInitialized.current = true;
  }, [configData, isFieldEnabled, cartDataList, search, currentPage, state, district, billboardType, locationData, siteTypeData, venueTypeData])

  // Memoize JSON.stringify values to prevent unnecessary re-runs of data fetching useEffect
  const locationDataString = useMemo(() => JSON.stringify(locationData), [locationData]);
  const siteTypeDataString = useMemo(() => JSON.stringify(siteTypeData), [siteTypeData]);
  const venueTypeDataString = useMemo(() => JSON.stringify(venueTypeData), [venueTypeData]);

  // Separate effect to handle data fetching - only triggers after initialization
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    nonDraftFetchCount.current += 1;
    if (cartRestorationInProgress.current) {
      console.info(`⏸️ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Cart restoration in progress`);
      return;
    }

    // 🛒 Guard 0.5: BLOCK if cart has items but cart country not yet resolved
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    const rawCart = localStorage.getItem('cartCountry');
    if ((cartHasItems || rawCart) && !getEffectiveCountryId()) {
      console.info(`⏸️ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Cart exists but country not resolved`);
      return;
    }

    if (!cartDataLoaded) {
      console.info(`⏳ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Waiting for cart data to load`);
      return;
    }
    if (cartCount > 0 && cartDataList.length === 0) {
      console.info(`⏳ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Waiting for cart details to sync country (Count: ${cartCount})`);
      return;
    }
    if (cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0 && countryOptions.length > 0) {
      const cartCountryName = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
      const cartCountryOption = countryOptions.find((c: any) => c.label === cartCountryName);

      if (cartCountryOption && selectedCountryId !== cartCountryOption.value) {
        console.info(`🛑 [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Waiting for Country Sync (Current: ${selectedCountryId}, Target: ${cartCountryOption.value})`);
        return;
      }
    }
    // 🔒 Skip if we just returned from cart (flag still exists in localStorage)
    const justReturnedFromCart = localStorage.getItem('clearStartCampaignFilters') === 'true';
    if (justReturnedFromCart) {
      console.info(`⏸️ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Just returned from cart, waiting for cart restoration`);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const editFlag = urlParams.get('edit') === 'true';
    if (editFlag !== edit) {
      setEdit(editFlag);
    }
    // Only fetch data for non-draft flows
    if (!edit && hasInitialized.current && billboardType && selectedCountryId && countryOptions.length > 0) {
      if (billBoardDataLoading) {
        console.info(`⏳ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Already loading`);
        return;
      }

      if (currentPage > 0) {
        console.info(`⏭️ [Non-Draft Effect] #${nonDraftFetchCount.current} SKIPPING - currentPage=${currentPage} > 0. Page Restore Effect will handle this.`);
        return;
      }

      setNoDataMessage(null); // Reset message before fetching
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        generateMockData(GETPROPERTY_LIST).then((mockResponse: any) => {
          if (mockResponse && mockResponse.data) {
            const data = mockResponse.data.landingpageExploreProperties || [];
            setBillBoardData(data);
            setFilteredBillboards(data);
            setTotalDataLength(data.length);
            setPageStartData(1);
            setPageEndData(Math.min(dataListLength, data.length));
          } else {
            console.error("Mock data non-draft useEffect: No data in response");
            setNoDataMessage('No mock data available.');
          }
        }).catch((err: any) => {
          console.error("Error generating mock data for non-draft:", err);
          setNoDataMessage('For your filters, no data found.');
          toast({
            title: "Error",
            description: "Failed to load mock data.",
            variant: "destructive"
          });
        });
      } else {
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));

        if (filters.category === 'packages') {
          const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package')
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          fetchPackageData(cartPackageIds, selectedCountryId, search);
        } else {
          debouncedFetchAllBillboardData(search, currentPage, cartBillboardIds, 'non-draft-useEffect', undefined, undefined, selectedCountryId);
        }
      }
    } else {
      console.info('Waiting for required data before fetching (non-draft):', {
        hasInitialized: hasInitialized.current,
        billboardType,
        selectedCountryId,
        edit
      });
    }

    return () => {
      if (debouncedFetchAllBillboardData && debouncedFetchAllBillboardData.cancel) {
        debouncedFetchAllBillboardData.cancel();
      }
    };
    // 🔴 FIX: Removed 'cartDataList?.length' from dependencies
  }, [
    search,
    currentPage,
    locationDataString,
    siteTypeDataString,
    venueTypeDataString,
    state,
    district,
    countryOptions.length,
    // selectedCountryId, <--- REMOVED: Causes re-trigger when cart restoration updates country
    // cartDataList?.length, <--- REMOVED
    draftData,
    draftInitialized,
    draftCountrySet,
    edit,
    cartDataLoaded,
  ]);



  // Separate effect for draft flows to ensure API calls use the correct country
  useEffect(() => {
    // 1. Strict Guard Clauses
    if (!edit || !selectedCountryId || !billboardType || countryOptions.length === 0) {
      return;
    }

    // 2. STOPPER: If we already fetched specifically for the draft, do not fetch again.
    // This prevents the loop where Fetch -> Cart Update -> Dependency Change -> Fetch
    if (dataFetchedForDraft.current) {
      console.info('🛑 Draft data already fetched. Skipping duplicate call.');
      return;
    }

    if (billBoardDataLoading) {
      console.info('⏳ Skipping draft API call: Already loading');
      return;
    }
    dataFetchedForDraft.current = true;

    setNoDataMessage(null);

    // MOCK DATA HANDLING
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(GETPROPERTY_LIST).then((mockResponse: any) => {
        if (mockResponse && mockResponse.data) {
          const data = mockResponse.data.landingpageExploreProperties || [];
          setBillBoardData(data);
          setFilteredBillboards(data);
          setTotalDataLength(data.length);
          setPageStartData(1);
          setPageEndData(Math.min(dataListLength, data.length));
        } else {
          console.error("Mock data draft useEffect: No data in response");
          setNoDataMessage('No mock data available.');
        }
      }).catch((err: any) => {
        console.error("Error generating mock data for draft:", err);
        setNoDataMessage('For your filters, no data found.');
        toast({
          title: "Error",
          description: "Failed to load mock data.",
          variant: "destructive"
        });
      });
    } else {
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      if (filters.category === 'packages') {
        const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));

        fetchPackageData(cartPackageIds, selectedCountryId, search);
      } else {
        fetchAllBillboardData(search, currentPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
      }
    }
  }, [selectedCountryId, edit, search, currentPage, filters.category, draftData, countryOptions.length, billboardType]);  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-filters', JSON.stringify(filters));
    }
  }, [JSON.stringify(filters)]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-expanded-sections', JSON.stringify(expandedSections));
    }
  }, [JSON.stringify(expandedSections)]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-search-query', searchQuery);
    }
  }, [searchQuery]);
  useEffect(() => {
    if (filters.priceRange) {
      setLocalPriceRange(filters.priceRange);
      setMinPriceInput(filters.priceRange[0] > 0 ? filters.priceRange[0].toString() : '');
      setMaxPriceInput(filters.priceRange[1] < 99999999 ? filters.priceRange[1].toString() : '');
    }
  }, [filters.priceRange]);

  // Handler for price range update on Enter key
  const handlePriceRangeUpdate = () => {


    // Only update filter if user has entered values
    if (minPriceInput !== '' || maxPriceInput !== '') {
      // Create price range object with only entered values
      const priceRangeForAPI: any = {};

      if (minPriceInput !== '') {
        const minValue = Math.max(0, parseInt(minPriceInput) || 0);
        priceRangeForAPI.minPrice = minValue;
      }

      if (maxPriceInput !== '') {
        const maxValue = Math.max(parseInt(maxPriceInput) || 0, priceRangeForAPI.minPrice || 0);
        priceRangeForAPI.maxPrice = maxValue;
      }

      // Update local state for UI display
      const minForDisplay = priceRangeForAPI.minPrice || 0;
      const maxForDisplay = priceRangeForAPI.maxPrice || 99999999;

      setLocalPriceRange([minForDisplay, maxForDisplay]);
      setMinPriceInput(priceRangeForAPI.minPrice ? priceRangeForAPI.minPrice.toString() : '');
      setMaxPriceInput(priceRangeForAPI.maxPrice && priceRangeForAPI.maxPrice < 99999999 ? priceRangeForAPI.maxPrice.toString() : '');

      // Update filters for internal state management
      setFilters((prev: typeof defaultFilters) => ({
        ...prev,
        priceRange: [minForDisplay, maxForDisplay]
      }));

      // Immediately trigger API call with only the entered price values
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, priceRangeForAPI)
        .then(() => {
          console.info('📊 Price filter API call completed successfully');
        })
        .catch((error) => {
          console.error('📊 Price filter API call failed:', error);
        });
    } else {
      console.info('📊 No price values entered, not updating filter');
    }
  };

  // Handler for min price input changes
  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric values or empty string
    if (value === '' || /^\d*$/.test(value)) {
      setMinPriceInput(value);
    }
  };

  // Handler for max price input changes
  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric values or empty string, and remove leading zeros
    if (value === '' || /^\d*$/.test(value)) {
      setMaxPriceInput(value === '' ? '' : value.replace(/^0+(?!$)/, ''));
    }
  };

  // Handler for Enter key press on price inputs
  const handlePriceKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handlePriceRangeUpdate();
    }
  };

  // Store previous filter values to detect intentional user changes
  const previousFiltersRef = useRef<any>(null);

  // Reset pagination ONLY when user intentionally changes filters (not on sync/restore)
  useEffect(() => {
    // On first mount, just initialize without resetting page
    if (!isFilterInitializedRef.current) {
      isFilterInitializedRef.current = true;
      previousFiltersRef.current = {
        search,
        state: JSON.stringify(filters.state),
        district: JSON.stringify(filters.district),
        category: filters.category,
        priceRange: JSON.stringify(filters.priceRange),
        venueType: filters.venueType,
        mediaOwner: JSON.stringify(filters.mediaOwner)
      };
      return;
    }

    // Initialize on first real comparison
    if (!previousFiltersRef.current) {
      previousFiltersRef.current = {
        search,
        state: JSON.stringify(filters.state),
        district: JSON.stringify(filters.district),
        category: filters.category,
        priceRange: JSON.stringify(filters.priceRange),
        venueType: filters.venueType,
        mediaOwner: JSON.stringify(filters.mediaOwner)
      };
      return;
    }

    // Check if any filter actually changed (not just reference changes)
    const hasSearchChanged = search !== previousFiltersRef.current.search;
    const hasStateChanged = JSON.stringify(filters.state) !== previousFiltersRef.current.state;
    const hasDistrictChanged = JSON.stringify(filters.district) !== previousFiltersRef.current.district;
    const hasCategoryChanged = filters.category !== previousFiltersRef.current.category;
    const hasPriceRangeChanged = JSON.stringify(filters.priceRange) !== previousFiltersRef.current.priceRange;
    const hasVenueTypeChanged = filters.venueType !== previousFiltersRef.current.venueType;
    const hasMediaOwnerChanged = JSON.stringify(filters.mediaOwner) !== previousFiltersRef.current.mediaOwner;

    // Only reset page if user intentionally changed a filter
    if (hasSearchChanged || hasStateChanged || hasDistrictChanged || hasCategoryChanged || hasPriceRangeChanged || hasVenueTypeChanged || hasMediaOwnerChanged) {
      setCurrentPage(0);
      localStorage.setItem('propcurrentPage', '0');
    }

    // Update previous filters reference for next comparison
    previousFiltersRef.current = {
      search,
      state: JSON.stringify(filters.state),
      district: JSON.stringify(filters.district),
      category: filters.category,
      priceRange: JSON.stringify(filters.priceRange),
      venueType: filters.venueType,
      mediaOwner: JSON.stringify(filters.mediaOwner)
    };
  }, [search, filters.state, filters.district, filters.category, filters.priceRange, filters.venueType, filters.mediaOwner]); // More specific dependencies - currentPage intentionally excluded

  const initialRestoreTriggeredRef = useRef(false);
  useEffect(() => {
    // Only trigger once per component lifetime
    if (initialRestoreTriggeredRef.current) {
      return;
    }

    // Must wait for initialization complete
    if (!hasInitialized.current) {
      return;
    }

    // Only restore if currentPage > 0 (page 0 is handled by other effects)
    if (currentPage === 0) {
      initialRestoreTriggeredRef.current = true;
      return;
    }

    // Need all required data before proceeding
    if (!selectedCountryId || !countryOptions.length || edit) {
      return;
    }

    // Mark that we've triggered this once
    initialRestoreTriggeredRef.current = true;
    const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
      cart.campaignInventories?.map((inv: any) => inv.billboardId)
    ).filter(Boolean));

    // Call API directly with the initially restored page
    fetchAllBillboardData(search, currentPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
  }, []);
  const lastApiCallPageRef = useRef<number>(-1); // Track last page we called API with
  useEffect(() => {
    // Only trigger ONCE per page value when initialized
    if (!hasInitialized.current) {
      return;
    }

    // Don't call API for page 0 (handled by other effects)
    if (currentPage === 0) {
      lastApiCallPageRef.current = -1;
      return;
    }

    // Don't proceed if missing required data
    if (!selectedCountryId || !countryOptions.length || billBoardDataLoading || edit) {
      return;
    }

    // ✅ CRITICAL: Call API if page number changed OR if we haven't called for this page yet
    // This ensures we fetch correct data even after returning from detail pages
    if (lastApiCallPageRef.current === currentPage) {
      return;
    }

    lastApiCallPageRef.current = currentPage;
    const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
      cart.campaignInventories?.map((inv: any) => inv.billboardId)
    ).filter(Boolean));

    // Call API directly with the current page
    fetchAllBillboardData(search, currentPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
  }, [currentPage, selectedCountryId, countryOptions.length, billBoardDataLoading, edit, search]);

  // Trigger API call when media owner filter changes
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    mediaOwnerFetchCount.current += 1;
    // Persist media owner to localStorage
    localStorage.setItem('start-campaign-filters', JSON.stringify(filters));
    const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
      cart.campaignInventories?.map((inv: any) => inv.billboardId)
    ).filter(Boolean));

    debouncedFetchAllBillboardData(search, currentPage, cartBillboardIds, 'mediaOwner filter changed', undefined, undefined, selectedCountryId);

  }, [filters.mediaOwner, selectedCountryId, currentPage, search]);

  // Handle search on Enter key press
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only trigger search if query is empty or >= 3 characters
      if (searchQuery.length === 0 || searchQuery.length >= 3) {
        setSearch(searchQuery);
        setCurrentPage(0); // Reset to first page

        const cartBillboardIds = new Set(
          cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        // Check if current category is packages
        if (filters.category === 'packages') {
          // Call package API for packages
          const cartPackageIds = new Set(
            cartDataList.flatMap((cart: any) =>
              cart.campaignInventories?.map((inv: any) => inv.packageId)
            ).filter(Boolean)
          );
          fetchPackageData(cartPackageIds, selectedCountryId, searchQuery);
        } else {
          fetchAllBillboardData(searchQuery, 0, cartBillboardIds, billboardType, undefined, undefined, selectedCountryId);
        }
      }
    }
  };

  // Handle search reset
  const handleResetSearch = () => {
    setSearchQuery('');
    setSearch('');
    setCurrentPage(0);

    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Check if current category is packages
    if (filters.category === 'packages') {
      const cartPackageIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.packageId)
        ).filter(Boolean)
      );
      fetchPackageData(cartPackageIds, selectedCountryId, '');
    } else {
      fetchAllBillboardData('', 0, cartBillboardIds, billboardType, undefined, undefined, selectedCountryId);
    }
  };


  const handleViewDetails = (inventoryItem: any) => {
    // Backup current country and filters before navigation (for draft/cart scenarios)
    const currentCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
    if (currentCountry) {
      localStorage.setItem('navigationCountryBackup', JSON.stringify({
        countryId: currentCountry.id,
        countryValue: currentCountry.value,
        countryLabel: currentCountry.label,
        timestamp: Date.now(),
        isDraft: edit,
        isCartCountry: hasCartCountry(),
        mediaOwner: filters.mediaOwner,
        environment: filters.environment,
        state: filters.state || currentStateRef.current,
        district: filters.district || currentDistrictRef.current
      }));
    }

    const billboardType = inventoryItem.inventoryType || inventoryItem.type || 'digital';

    let targetUrl = '';
    let stateData: any = {
      currentPrice: inventoryItem.type === 'DIGITAL'
        ? priceFormat === 'spot-based'
          ? inventoryItem.perSpotPrice
          : inventoryItem.perDayPrice
        : (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
          ? inventoryItem.sellingRate.monthly.month1
          : inventoryItem.currentPrice,
      currencyCode: inventoryItem.price?.localCurrencies?.code,
      startDate,
      endDate,
      campaignName,
      priceFormat
    };

    // Construct query params for draft/edit flow with dates
    const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
    const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';

    // 🔄 ADD PAGE AND BILLBOARD TYPE TO NAVIGATION PARAMS
    const pageParam = `page=${currentPage}`;
    const billboardTypeParam = `billboardType=${encodeURIComponent(billboardType)}`;
    const spotBasedParam = `spotBased=${spotBasedBooking}`;
    const buyingFormatParam = `buyingFormat=${encodeURIComponent(buyingFormat)}`;

    const draftParams = edit
      ? `?draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&source=${encodeURIComponent(source)}&edit=true&${pageParam}&${billboardTypeParam}&${spotBasedParam}&${buyingFormatParam}`
      : `?${pageParam}&${billboardTypeParam}&${spotBasedParam}&${buyingFormatParam}`;

    // Choose navigation route based on item type/category
    if (filters.category === 'deals' || inventoryItem.discountedPrice || inventoryItem.originalPrice) {
      targetUrl = `/deal/${inventoryItem.id}${draftParams}`;
    } else if (filters.category === 'packages' || inventoryItem.registryPackageType === "DAYWISE") {
      targetUrl = `/package/${inventoryItem.id}${draftParams}`;
    } else {
      const separator = draftParams ? '&' : '';
      targetUrl = `/property-details/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}${separator}${draftParams.substring(1)}`; // Remove leading ? from draftParams
    }

    navigate(targetUrl, { state: stateData });
  };


  // Generic function to get inventory status - returns both text and CSS classes
  const getInventoryStatus = (inventoryItem: any) => {
    // Calculate price validity (same as your button logic)
    let price = 0;
    if (inventoryItem.type === 'DIGITAL') {
      price = priceFormat === 'spot-based'
        ? inventoryItem.perSpotPrice
        : inventoryItem.perDayPrice;
    } else {
      price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
        ? inventoryItem.sellingRate.monthly.month1
        : inventoryItem.currentPrice;
    }
    const isPriceValid = !!price && price !== 0;

    if (!isPriceValid) {
      return { text: "Unavailable", color: "bg-gray-100 text-gray-700" };
    } else if (!inventoryItem.availableBooking || !inventoryItem.active) {
      return { text: "Sold Out", color: "bg-red-100 text-red-700" };
    } else {
      return { text: "Available", color: "bg-emerald-100 text-emerald-700" };
    }
  };

  // Generic Status Badge Component with optional dot
  const StatusBadge = ({ inventoryItem, showDot = false, className = "" }: { inventoryItem: any; showDot?: boolean; className?: string }) => {
    const status = getInventoryStatus(inventoryItem);
    return (
      <Badge className={`${status.color} ${className}`}>
        {showDot && <div className="w-2 h-2 rounded-full bg-current mr-1"></div>}
        {status.text}
      </Badge>
    );
  };

  const toggleSection = (section: keyof typeof defaultExpandedSections) => {
    setExpandedSections((prev: any) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateFilter = (key: string, value: any, shouldTriggerApi: boolean = true) => {
    setFilters((prev: typeof defaultFilters) => ({
      ...prev,
      [key]: value
    }));

    // Only trigger API call for certain filter changes
    if (shouldTriggerApi && key !== 'state') {
      // For category change to 'packages', trigger package API
      if (key === 'category' && value === 'packages') {
        setTimeout(() => {
          const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package')
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          fetchPackageData(cartPackageIds, selectedCountryId, search);
        }, 300);
      }
      // For district changes or other changes when in billboard category, trigger billboard API
      else if (key === 'district' || key === 'priceRange' || (key === 'category' && value !== 'packages')) {
        setTimeout(() => {
          const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          debouncedFetchAllBillboardData(search, currentPage, cartBillboardIds, `${key}Change`);
        }, 300);
      }
    } else {
      console.info(`⏸️ Not triggering API call for ${key} (shouldTriggerApi: ${shouldTriggerApi})`);
    }
  };

  const toggleArrayFilter = (key: string, value: string) => {
    setFilters((prev: typeof defaultFilters) => {
      const currentArray = prev[key as keyof typeof prev] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];

      const newFilters = {
        ...prev,
        [key]: newArray
      };
      if (typeof window !== 'undefined') {
        localStorage.setItem('start-campaign-filters', JSON.stringify(newFilters));
      }

      // If this is venue type filter, update venueTypeData with expanded children
      if (key === 'venueType') {
        const expandedVenueTypes: string[] = [];
        newArray.forEach(parentVenueType => {
          // Add the parent itself (now using API spelling directly)
          expandedVenueTypes.push(parentVenueType);
          // Add all children and grandchildren (also using API spelling directly)
          const children = getAllChildrenForVenueType(parentVenueType);
          expandedVenueTypes.push(...children);
        });

        // Remove duplicates and update venueTypeData
        const uniqueVenueTypes = [...new Set(expandedVenueTypes)];
        setVenueTypeData(uniqueVenueTypes);
      }

      return newFilters;
    });
  };

  // New function for single value filters (like radio buttons)
  const handleSingleFilter = (key: string, value: string) => {
    setFilters((prev: typeof defaultFilters) => {
      const newFilters = {
        ...prev,
        [key]: value
      };

      if (typeof window !== 'undefined') {
        localStorage.setItem('start-campaign-filters', JSON.stringify(newFilters));
      }

      // If this is venue type filter, update venueTypeData with expanded children
      // If this is environment filter, trigger API call
      if (key === 'environment') {
        currentEnvironmentRef.current = value;
        // Trigger API call for environment change
        setTimeout(() => {
          const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          debouncedFetchAllBillboardData(search, 0, cartBillboardIds, 'environmentChange', undefined, undefined, selectedCountryId);
        }, 300);
      }
      // If this is venue type filter, update venueTypeData with expanded children
      else if (key === 'venueType') {
        currentVenueTypeRef.current = value;
        const parentVenueType = value ? [value] : [];
        setVenueTypeData(parentVenueType);
      }

      return newFilters;
    });
  };

  const clearAllFilters = async () => {
    const freshCartCount = await fetchCartCountDetails();
    const hasCartData = freshCartCount > 0;
    let finalCountryId = selectedCountryId;
    let finalCountryObj = null;

    if (hasCartData) {
      setFilters({
        ...defaultFilters,
        country: selectedCountryId, // Preserve cart country
        environment: "",
      });
      currentEnvironmentRef.current = '';
      currentVenueTypeRef.current = '';
      currentStateRef.current = [];
      currentDistrictRef.current = [];
      finalCountryId = selectedCountryId;
    } else {

      let targetCountryId = null;
      let targetCountryObj = null;
      let source = '';

      // Get session default country
      const sessionDefaultId = localStorage.getItem('sessionDefaultCountryId');
      if (sessionDefaultId) {
        targetCountryObj = countryOptions.find((c: any) => c.id === sessionDefaultId);
        if (targetCountryObj) {
          targetCountryId = sessionDefaultId;
          source = 'sessionDefault';
        }
      }

      setFilters({
        ...defaultFilters,
        country: targetCountryObj?.value || '', // Use determined country
        state: [],
        district: [],
        environment: "",
      });
      currentEnvironmentRef.current = '';
      currentVenueTypeRef.current = '';
      currentStateRef.current = [];
      currentDistrictRef.current = [];

      if (targetCountryObj) {
        setSelectedCountryId(targetCountryObj.value);
        finalCountryId = targetCountryObj.value;
        finalCountryObj = targetCountryObj;
      } else {
        finalCountryId = '';
      }
    }

    // Clear other filter states
    setSearchQuery("");
    setSearch(""); // Clear the actual search state that triggers API calls
    setLocalPriceRange([0, 0]);
    setMinPriceInput('');
    setMaxPriceInput('');

    // Reset location filters
    setState([]);
    setDistrict([]);
    setAvailableDistricts([]);


    // Reset billboard type to default (digital)
    setBillboardType("digital");

    // Reset other filter states
    setLocationData([]);
    setSitetypeData([]);
    setVenueTypeData([]); // ✅ IMPORTANT: Clear venueTypeData state
    setIndoorChecked(false);
    setOutdoorChecked(false);

    setSpotBasedBooking(false);
    setPriceFormat('day-based');
    setBuyingFormat('day-based');

    // Reset pagination
    setCurrentPage(0);

    // Reset no data message
    setNoDataMessage(null);

    // Clear from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('start-campaign-filters');
      localStorage.removeItem('start-campaign-search-query');
      localStorage.removeItem('campaignListState');
      localStorage.removeItem('start-campaign-expanded-sections');
      localStorage.removeItem('propcurrentPage');
      localStorage.removeItem('campaignState');
      localStorage.removeItem('campaignDistrict');
      localStorage.setItem('userChangedCountry', 'false');
      localStorage.setItem('clearedAllCart', 'true');
      localStorage.removeItem('selectedCountryId');
    }
    countryInitialized.current = false;
    filtersRef.current = {
      ...defaultFilters,
      country: finalCountryId,
      environment: "",
      mediaOwner: [],
      venueType: "",
      state: [],
      district: [],
    };

    setTimeout(() => {
      fetchAllBillboardData(
        "", // search value
        0, // page
        new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId) || []
        )),
        undefined,
        [], // overrideState - empty array to clear state filter
        undefined,
        finalCountryId,
        [], // overrideDistrict - empty array to clear district filter
        "", // overrideCompanyId
        [], // overrideVenueType
        "" // overrideEnvironment
      );
    }, 100);
  };

  // ...existing code...

  useEffect(() => {
    const shouldClear = localStorage.getItem('clearStartCampaignFilters');
    if (shouldClear) {
      clearAllFilters(); // Your existing function
      localStorage.removeItem('clearStartCampaignFilters');
      sessionStorage.setItem('filtersClearedThisSession', 'true'); // Prevent repeats
    } else {
      console.info('✅ No clear flag found, filters intact');
    }
  }, []); // Still runs only on mount

  // Place the package API useEffect here - after all state and function declarations
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    packageFetchCount.current += 1;

    if (!cartDataLoaded) {
      console.info(`⏳ [Package Effect] #${packageFetchCount.current} Skipped: Waiting for cart data to load`);
      return;
    }

    if (cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0 && countryOptions.length > 0) {
      const cartCountryName = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
      const cartCountryOption = countryOptions.find((c: any) => c.label === cartCountryName);

      if (cartCountryOption && selectedCountryId !== cartCountryOption.value) {
        return;
      }
    }

    if (filters.category === 'packages' && selectedCountryId && countryOptions.length > 0) {
      const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType === 'package')
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));
      fetchPackageData(cartPackageIds, selectedCountryId, search);
    }
  }, [filters.category, selectedCountryId, countryOptions.length, search, cartDataLoaded]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header yourLogo={yourLogo} configData={configData} base_path={base_path} cartUrl={cartUrl} />

      <div className="max-w-[95vw] mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-emerald-600">Start New Campaign</h1>
            <p className="text-gray-600 mt-2">Select Inventories for your advertising campaign</p>
          </div>
          <div className="flex items-center gap-4">
            {/* {cartCount > 0 && (
              <Link
                to={
                  edit && campaignId
                    ? `/mycart?draftId=${campaignId}&draftName=${encodeURIComponent(campaignName)}&startDate=${startDate || ''}&endDate=${endDate || ''}&source=${source}&edit=${edit}`
                    : "/mycart"
                }
              >
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Cart ({cartCount})
                </Button>
              </Link>
            )} */}
            <Button
              variant="outline"
              onClick={() => {
                if (edit) {
                  setCartCount(0);
                  setCartItemCountContext(0);
                  setCartItems([]);
                  setCartDataList([]);
                  setCartData([]);
                }
                navigate(`${base_path}/my-campaigns`);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Sidebar - Filters */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-emerald-600">Filter By</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search */}
                <div>
                  <Label htmlFor="search" className="text-sm font-medium text-gray-900">
                    Search
                  </Label>
                  <div className="relative mt-1 h-10">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search Inventory"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className={`px-10 pr-10 ${searchQuery.length > 0 && searchQuery.length < 3
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500'
                        }`}
                    />
                    {searchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                        title="Clear search"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </Button>
                    )}
                  </div>
                  {searchQuery.length > 0 && searchQuery.length < 3 && (
                    <p className="text-xs text-red-600 mt-1">
                      Enter at least 3 characters, then press Enter to search
                    </p>
                  )}

                </div>

                <Separator />

                {/* Location Filters */}
                <FilterSection
                  title="Location"
                  isExpanded={expandedSections.location}
                  onToggle={() => toggleSection('location')}
                >
                  {/* Country Dropdown */}
                  <div>
                    <div className="flex items-center space-x-1">
                      <Label className="text-sm font-medium text-gray-700">
                        Country
                      </Label>
                      {(countryLoading || !countryOptions.length || (contextCartItems && contextCartItems.length > 0) || edit) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="h-3 w-3 text-gray-400 cursor-pointer" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                              <div className="space-y-1">
                                <p className="text-gray-200">Remove all Inventories or start a new proposal to change country.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="relative mt-1" data-dropdown="country">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (contextCartItems && contextCartItems.length > 0) {
                            toast({
                              title: "Cannot change country",
                              description: "You have items in your cart from a specific country. Please complete your current campaign or clear your cart to change countries.",
                              variant: "destructive"
                            });
                            return;
                          }
                          if (edit) {
                            toast({
                              title: "Cannot change country",
                              description: "You cannot change the country for a draft campaign. The country is fixed based on the draft.",
                              variant: "destructive"
                            });
                            return;
                          }
                          // Close other dropdowns when opening country dropdown
                          if (!showCountryDropdown) {
                            setShowStateDropdown(false);
                            setShowDistrictDropdown(false);
                            setCountrySearchTerm('');
                          } else {
                            setCountrySearchTerm('');
                          }
                          setShowCountryDropdown(!showCountryDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={countryLoading || !countryOptions.length || (contextCartItems && contextCartItems.length > 0) || edit}
                      >
                        {countryLoading ? (
                          "Loading countries..."
                        ) : filters.country ? (
                          countryOptions.find((c: any) => c.value === filters.country)?.label || "Select Country"
                        ) : (
                          "Select Country"
                        )}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showCountryDropdown && countryOptions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search country..."
                                value={countrySearchTerm}
                                onChange={(e) => setCountrySearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {countrySearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCountrySearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {filteredCountryOptions.length > 0 ? (
                              filteredCountryOptions.map((country: any) => {
                                const isSelected = filters.country === country.value;
                                return (
                                  <div
                                    key={country.value}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => handleCountrySelection(country.value)}
                                  >
                                    <span className="text-sm flex-1">{country.label}</span>
                                    {isSelected && (
                                      <span className="text-emerald-600">✓</span>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No countries found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">State</Label>
                    <div className="relative mt-1" data-dropdown="state">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening state dropdown
                          if (!showStateDropdown) {
                            setShowCountryDropdown(false);
                            setShowDistrictDropdown(false);
                            setStateSearchTerm('');
                          } else {
                            setStateSearchTerm('');
                          }
                          setShowStateDropdown(!showStateDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={stateApiLoading || !availableStates.length || !selectedCountryId}
                      >
                        <span className="truncate">
                          {!selectedCountryId
                            ? 'Select Country First'
                            : stateApiLoading
                              ? 'Loading States...'
                              : filters.state.length === 0
                                ? 'Select State'
                                : filters.state.length === 1
                                  ? (() => {
                                    const selectedId = filters.state[0];
                                    const stateObj = availableStates.find(s => String(s.stateId || s.id) === String(selectedId));

                                    // FIX: If stateObj is found, use it. If not, check localStorage for the saved name.
                                    // This handles the "page reload/return" scenario where IDs exist but the list hasn't loaded.
                                    if (stateObj) return (stateObj.name || stateObj.stateName);

                                    const savedName = localStorage.getItem('campaignStateName');
                                    return savedName || 'Selected State';
                                  })()
                                  : `${filters.state.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showStateDropdown && availableStates.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search state..."
                                value={stateSearchTerm}
                                onChange={(e) => setStateSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {stateSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setStateSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option - Only show if states are selected */}
                            {filters.state.length > 0 && (
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                setState([]);
                                setDistrict([]);
                                setAvailableDistricts([]);
                                currentStateRef.current = [];
                                currentDistrictRef.current = [];
                                updateFilter('state', [], false);
                                updateFilter('district', [], false);
                                setShowStateDropdown(false);
                                setStateSearchTerm('');
                                const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                  cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                ).filter(Boolean));
                                fetchAllBillboardData(search, 0, cartBillboardIds, undefined, [], undefined, selectedCountryId, []);
                              }}
                            >
                              <span className="text-sm text-gray-500">Clear selection</span>
                            </div>
                            )}
                            {filteredStateOptions.length > 0 ? (
                              filteredStateOptions.map((stateItem) => {
                                const stateId = stateItem.stateId || stateItem.id;
                                const stateName = stateItem.name || stateItem.stateName;
                                const isSelected = filters.state.includes(stateId);
                                return (
                                  <div
                                    key={stateId}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={async () => {
                                      if (debouncedFetchAllBillboardData && debouncedFetchAllBillboardData.cancel) {
                                        debouncedFetchAllBillboardData.cancel();
                                      }
                                      const newStates = isSelected
                                        ? filters.state.filter((id: string) => id !== stateId)
                                        : [...filters.state, stateId];
                                      isManualStateChange.current = true;
                                      currentStateRef.current = newStates;
                                      setState(newStates);
                                      updateFilter('state', newStates, false);
                                      setShowStateDropdown(false);
                                      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                        cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                      ).filter(Boolean));
                                      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, newStates).finally(() => {
                                        setTimeout(() => {
                                          isManualStateChange.current = false;
                                        }, 1000);
                                      });
                                      if (newStates.length > 0) {
                                        try {
                                          const { data } = await getDistricts({
                                            variables: { stateIds: Array.from(new Set(newStates)).filter(Boolean).join(','), exposure: exposure },
                                            fetchPolicy: 'network-only',
                                          });
                                          const newAvailableDistricts = data?.districtListMarketplace || [];
                                          setAvailableDistricts(newAvailableDistricts);
                                          const validDistrictIds = newAvailableDistricts.map((d: any) => d.id);
                                          const newDistricts = filters.district.filter((districtId: string) => validDistrictIds.includes(districtId));
                                          setDistrict(newDistricts);
                                          updateFilter('district', newDistricts, false);
                                        } catch (error) {
                                          console.error('Error fetching districts:', error);
                                          setAvailableDistricts([]);
                                          setDistrict([]);
                                          updateFilter('district', [], false);
                                        }
                                      } else {
                                        setAvailableDistricts([]);
                                        setDistrict([]);
                                        updateFilter('district', [], false);
                                      }
                                    }}
                                  >
                                    <Checkbox
                                      className="!border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                      checked={isSelected}
                                      onCheckedChange={() => { }}
                                    />
                                    <span className="text-sm">{stateName}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No states found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">District</Label>
                    <div className="relative mt-1" data-dropdown="district">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening district dropdown
                          if (!showDistrictDropdown) {
                            setShowCountryDropdown(false);
                            setShowStateDropdown(false);
                            setDistrictSearchTerm('');
                          } else {
                            setDistrictSearchTerm('');
                          }
                          setShowDistrictDropdown(!showDistrictDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={!availableDistricts.length || filters.state.length === 0}
                      >
                        <span className="truncate">
                          {filters.state.length === 0
                            ? 'Select State First'
                            : filters.district.length === 0
                              ? 'Select District'
                              : filters.district.length === 1
                                ? (() => {
                                  const selectedId = filters.district[0];
                                  const distObj = availableDistricts.find(d => String(d.id || d.districtId) === String(selectedId));

                                  // FIX: If distObj is found, use it. If not, check localStorage fallback.
                                  if (distObj) return (distObj.name || distObj.districtName);

                                  const savedName = localStorage.getItem('campaignDistrictName');
                                  return savedName || 'Selected District';
                                })()
                                : `${filters.district.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showDistrictDropdown && availableDistricts.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search district..."
                                value={districtSearchTerm}
                                onChange={(e) => setDistrictSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {districtSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDistrictSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option - Only show if districts are selected */}
                            {filters.district.length > 0 && (
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                setDistrict([]);
                                isManualDistrictChange.current = true;
                                currentDistrictRef.current = [];
                                updateFilter('district', [], false);
                                setShowDistrictDropdown(false);
                                setDistrictSearchTerm('');
                                const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                  cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                ).filter(Boolean));
                                fetchAllBillboardData(search, 0, cartBillboardIds, undefined, filters.state, undefined, selectedCountryId, []);
                              }}
                            >
                              <span className="text-sm text-gray-500">Clear selection</span>
                            </div>
                            )}
                            {filteredDistrictOptions.length > 0 ? (
                              filteredDistrictOptions.map((districtItem) => {
                                const districtId = districtItem.id;
                                const districtName = districtItem.name || districtItem.districtName;
                                const isSelected = filters.district.includes(districtId);
                                return (
                                  <div
                                    key={districtId}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => {
                                      let newDistricts = isSelected
                                        ? filters.district.filter((id: string) => id !== districtId)
                                        : [...filters.district, districtId];
                                      isManualDistrictChange.current = true;
                                      currentDistrictRef.current = newDistricts;
                                      setDistrict(newDistricts);
                                      updateFilter('district', newDistricts);
                                      setShowDistrictDropdown(false);
                                      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                        cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                      ).filter(Boolean));
                                      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, filters.state, undefined, undefined, newDistricts).finally(() => {
                                        setTimeout(() => {
                                          isManualDistrictChange.current = false;
                                        }, 500);
                                      });
                                    }}
                                  >
                                    <Checkbox
                                      className="!border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                      checked={isSelected}
                                      onCheckedChange={() => { }}
                                    />
                                    <span className="text-sm">{districtName}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No districts found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </FilterSection>

                {/* Category Filters */}
                <FilterSection
                  title="Inventory Type"
                  isExpanded={expandedSections.category}
                  onToggle={() => toggleSection('category')}
                >
                  <div className="space-y-2">
                    {['digital', 'classic'].map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={category}
                          name="inventoryTypeRadio"
                          checked={filters.category === category}
                          onChange={() => {
                            handleBillboardTypeChange(category);
                            if (category === 'digital') {
                              setPriceFormat('day-based');
                            }
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor={category} className="text-sm text-gray-700 cursor-pointer capitalize">
                          {category}
                        </Label>
                      </div>
                    ))}
                  </div>
                </FilterSection>

                {/* Buying Format Filter */}
                {filters.category === 'digital' && (
                  <FilterSection
                    title="Buying Format"
                    isExpanded={expandedSections.buyingFormat}
                    onToggle={() => toggleSection('buyingFormat')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="spot-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'spot-based'}
                          onChange={() => {
                            setBuyingFormat('spot-based');
                            setPriceFormat('spot-based');
                            setSpotBasedBooking(true);
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="spot-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Spot based
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="day-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'day-based'}
                          onChange={() => {
                            setBuyingFormat('day-based');
                            setPriceFormat('day-based');
                            setSpotBasedBooking(false);
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="day-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Day based
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Value Packs Filters */}
                <FilterSection
                  title="Value Packs"
                  isExpanded={expandedSections.valuePacks}
                  onToggle={() => toggleSection('valuePacks')}
                >
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="packages"
                        name="inventoryTypeRadio"
                        checked={filters.category === 'packages'}
                        onChange={() => {
                          handleBillboardTypeChange('packages');
                        }}
                        className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                      />
                      <Label htmlFor="packages" className="text-sm text-gray-700 cursor-pointer capitalize">
                        packages
                      </Label>
                    </div>
                  </div>
                </FilterSection>

                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Environment"
                    isExpanded={expandedSections.environment}
                    onToggle={() => toggleSection('environment')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="environment-all"
                          name="environmentRadio"
                          checked={filters.environment === '' || !filters.environment}
                          onChange={() => handleSingleFilter('environment', '')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="environment-all" className="text-sm text-gray-700 cursor-pointer">
                          All
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="indoor"
                          name="environmentRadio"
                          checked={filters.environment === 'INDOOR'}
                          onChange={() => handleSingleFilter('environment', 'INDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="indoor" className="text-sm text-gray-700 cursor-pointer">
                          Indoor
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="outdoor"
                          name="environmentRadio"
                          checked={filters.environment === 'OUTDOOR'}
                          onChange={() => handleSingleFilter('environment', 'OUTDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="outdoor" className="text-sm text-gray-700 cursor-pointer">
                          Outdoor
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Pricing Filter */}
                <FilterSection
                  title="Pricing"
                  isExpanded={expandedSections.pricing}
                  onToggle={() => toggleSection('pricing')}
                  actionButton={
                    (minPriceInput !== '' || maxPriceInput !== '') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setMinPriceInput('');
                          setMaxPriceInput('');
                          setFilters((prev: typeof defaultFilters) => ({
                            ...prev,
                            priceRange: [0, 99999999]
                          }));
                          setLocalPriceRange([0, 99999999]);
                          
                          // Call API with same pattern as Enter key - clearing price filter
                          const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                            cart.campaignInventories?.map((inv: any) => inv.billboardId)
                          ).filter(Boolean));

                          // Pass empty object for priceRangeForAPI to clear price filter
                          fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, {})
                            .then(() => {
                              console.info('📊 Price filter cleared - API call completed successfully');
                            })
                            .catch((error) => {
                              console.error('📊 Price filter cleared - API call failed:', error);
                            });
                        }}
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded"
                        title="Clear price filter"
                      >
                        Clear
                      </Button>
                    )
                  }
                >
                  {(() => {
                    const selectedCurrencyCode = countryOptions.find((c: any) => c.value === filters.country)?.currencyCode || 'N/A';

                    return (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700">
                          Price Range: {selectedCurrencyCode} {filters.priceRange?.[0] ?? 0} - {selectedCurrencyCode} {filters.priceRange && filters.priceRange[1] < 99999999 ? filters.priceRange[1] : 'No Limit'}
                        </Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min={0}
                            value={minPriceInput}
                            onChange={handleMinPriceChange}
                            onKeyPress={handlePriceKeyPress}
                            className="w-24"
                            placeholder="Min"
                            aria-label="Minimum price"
                            autoComplete="off"
                          />
                          <span className="text-gray-500">-</span>
                          <Input
                            type="number"
                            min={0}
                            value={maxPriceInput}
                            onChange={handleMaxPriceChange}
                            onKeyPress={handlePriceKeyPress}
                            className="w-24"
                            placeholder="No Limit"
                            aria-label="Maximum price"
                            autoComplete="off"
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{selectedCurrencyCode} 0</span>
                          <span>{selectedCurrencyCode} No Limit</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Press Enter to apply price filter
                        </p>
                      </div>
                    );
                  })()}
                </FilterSection>

                {/* Media Owner Filter */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Media Owner"
                    isExpanded={expandedSections.mediaOwner}
                    onToggle={() => toggleSection('mediaOwner')}
                  >
                    <div className="relative" data-dropdown="mediaOwner">
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Close other dropdowns when opening media owner dropdown
                          if (!showMediaOwnerDropdown) {
                            setShowCountryDropdown(false);
                            setShowStateDropdown(false);
                            setShowDistrictDropdown(false);
                            setMediaOwnerSearchTerm('');
                          } else {
                            setMediaOwnerSearchTerm('');
                          }
                          setShowMediaOwnerDropdown(!showMediaOwnerDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={mediaOwnerLoading || mediaOwnerOptions.length === 0}
                      >
                        <span className="truncate">
                          {mediaOwnerLoading
                            ? 'Loading media owners...'
                            : mediaOwnerOptions.length === 0
                              ? 'No media owners available'
                              : filters.mediaOwner?.length === 0
                                ? 'Select Media Owner'
                                : filters.mediaOwner?.length === 1
                                  ? mediaOwnerOptions.find((o: any) => o.value === filters.mediaOwner?.[0])?.label || 'Selected'
                                  : `${filters.mediaOwner?.length} selected`}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showMediaOwnerDropdown && mediaOwnerOptions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                          {/* Search Input */}
                          <div className="p-2 border-b border-emerald-200 sticky top-0 bg-white">
                            <div className="relative">
                              <Input
                                type="text"
                                placeholder="Search media owner..."
                                value={mediaOwnerSearchTerm}
                                onChange={(e) => setMediaOwnerSearchTerm(e.target.value)}
                                className="h-8 text-sm border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 pr-8"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              {mediaOwnerSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setMediaOwnerSearchTerm('')}
                                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                  title="Clear search"
                                >
                                  <X className="h-3 w-3 text-gray-400" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Options List */}
                          <div className="overflow-y-auto">
                            {/* Clear option */}
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                setFilters((prev: any) => ({ ...prev, mediaOwner: [] }));
                                setShowMediaOwnerDropdown(false);
                                setMediaOwnerSearchTerm('');
                              }}
                            >
                              <span className="text-sm text-gray-500">Clear selection</span>
                            </div>
                            {/* --- Media Owner Dropdown List --- */}
                            {filteredMediaOwnerOptions.length > 0 ? (
                              filteredMediaOwnerOptions.map((owner: any) => {
                                const isSelected = filters.mediaOwner?.includes(owner.value) || false;
                                return (
                                  <div
                                    key={owner.value}
                                    className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                    onClick={() => {
                                      toggleArrayFilter('mediaOwner', owner.value);
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => { }} // Handled by parent onClick
                                      className="pointer-events-none !border-emerald-600 !data-[state=checked]:bg-emerald-600"
                                    />
                                    <span className="text-sm flex-1">{owner.label}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No media owners found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </FilterSection>
                )}


                {/* Venue Type - Hidden when packages category is selected */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Venue Type"
                    isExpanded={expandedSections.venue}
                    onToggle={() => toggleSection('venue')}
                  >
                    <div className="space-y-2">
                      <RadioGroup
                        value={filters.venueType || ""}
                        onValueChange={(value) => handleSingleFilter('venueType', value)}
                      >
                        {venueTypesHierarchy.map((venueType) => (
                          <div key={venueType.id} className="flex items-center space-x-2">
                            <RadioGroupItem
                              id={venueType.name}
                              value={venueType.name}
                              className="!border-emerald-600 !text-emerald-600"
                            />
                            <Label htmlFor={venueType.name} className="text-sm text-gray-700 cursor-pointer">
                              {venueType.value}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </FilterSection>
                )}

                {/* Target Audience Filter (commented out for future use)
                <FilterSection
                  title="Target Audience"
                  isExpanded={expandedSections.audience}
                  onToggle={() => toggleSection('audience')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Age Range</Label>
                      <CheckboxGroup
                        options={['18-24', '25-34', '35-44', '45-54', '55+']}
                        value={filters.targetAgeRange || []}
                        onChange={(option: string) => toggleArrayFilter('targetAgeRange', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Gender</Label>
                      <CheckboxGroup
                        options={['Male', 'Female', 'All']}
                        value={filters.targetGender || []}
                        onChange={(option: string) => toggleArrayFilter('targetGender', option)}
                      />
                    </div>
                  </div>
                </FilterSection>
                */}

                {/* Inventory Specification Filter (commented out for future use)
                <FilterSection
                  title="Inventory Specification"
                  isExpanded={expandedSections.inventorySpec}
                  onToggle={() => toggleSection('inventorySpec')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Screen Size</Label>
                      <CheckboxGroup
                        options={['Small (14x8)', 'Medium (20x10)', 'Large (30x15)', 'Extra Large (40x20)']}
                        value={filters.screenSize || []}
                        onChange={(option: string) => toggleArrayFilter('screenSize', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Orientation</Label>
                      <CheckboxGroup
                        options={['Landscape', 'Portrait', 'Square']}
                        value={filters.orientation || []}
                        onChange={(option: string) => toggleArrayFilter('orientation', option)}
                      />
                    </div>
                  </div>
                </FilterSection>
                */}
              </CardContent>
            </Card>
          </div>

          {/* Right Content Area */}
          <div className="flex-1">
            {/* Top Bar with View Controls */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-emerald-600">{filters.category === 'packages' ? 'Available Packages' : 'Available Inventories'}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {filters.category === 'packages' ? packageData.length : billBoardData.length} {filters.category === 'packages' ? (packageData.length === 1 ? 'Package' : 'Packages') : (billBoardData.length === 1 ? 'Inventory' : 'Inventories')} {search && `for "${search}"`}
                    {/* {searchQuery.length > 0 && searchQuery.length < 3 && (
                      <span className="text-red-600"> • Enter 3+ characters to search</span>
                    )} */}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center border border-gray-200 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className={`h-8 w-8 p-0 ${viewMode === 'grid' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className={`h-8 w-8 p-0 ${viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'map' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('map')}
                      className={`h-8 w-8 p-0 ${viewMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <MapIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isPageLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-600">
                  {filters.category === 'packages' ? 'Loading packages...' : 'Loading inventories...'}
                </p>
              </div>
            ) : filters.category === 'packages' ? (
              // Render packages
              packageData.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Packages Found</h3>
                  <p className="text-gray-600">Try adjusting your search or filters.</p>
                </div>
              ) : (
                <>
                  {viewMode === 'map' ? (
                    <MapView
                      inventory={packageData}
                      onInventorySelect={handleViewDetails}
                      onAddToCart={addToCart}
                      onDeleteFromCart={deleteCartItem}
                      isInCart={isInCart}
                      currencyCode={packageData?.[0]?.price?.localCurrencies?.code || 'N/A'}
                      edit={edit}
                      draftData={draftData}
                      cartDataList={cartDataList}
                      loadingCartItemIds={loadingCartItemIds}
                      campaignStartDate={startDate}
                      campaignEndDate={endDate}
                      isViewingPackages={true}
                    />
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {packageData.map((pkg: any) => (
                        <PackageCard
                          key={pkg.id}
                          package={[pkg]}
                          viewMode={viewMode}
                          campaignStartDate={startDate}
                          campaignEndDate={endDate}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {packageData.map((pkg: any) => (
                        <PackageCard
                          key={pkg.id}
                          package={[pkg]}
                          viewMode={viewMode}
                          campaignStartDate={startDate}
                          campaignEndDate={endDate}
                        />
                      ))}
                    </div>
                  )}

                  {/* Pagination Controls for Packages */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t mt-6 rounded-lg">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {pageStartData} - {pageEndData} of {totalDataLength} {totalDataLength === 1 ? 'Package' : 'Packages'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={paginationMinus}
                        disabled={currentPage === 0}
                        className="flex items-center space-x-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </Button>

                      <div className="flex items-center space-x-1">
                        {getPageNumbers().map((page, index) => (
                          <div key={index}>
                            {page === '...' ? (
                              <span className="px-2 text-gray-500">...</span>
                            ) : (
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageClick(page as number)}
                                className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                              >
                                {(page as number) + 1}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={paginationPlus}
                        disabled={currentPage >= Math.ceil(totalDataLength / dataListLength) - 1}
                        className="flex items-center space-x-1"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )
            ) : billBoardData.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No inventory found</h3>
                <p className="text-gray-600">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <>
                {viewMode === 'map' ? (
                  <MapView
                    inventory={billBoardData}
                    onInventorySelect={handleViewDetails}
                    onAddToCart={addToCart}
                    onDeleteFromCart={deleteCartItem}
                    isInCart={isInCart}
                    currencyCode={billBoardData?.[0]?.price?.localCurrencies?.code || 'N/A'}
                    edit={edit} // <-- Pass edit flag
                    draftData={draftData} // <-- Pass draftData for edit flow
                    cartDataList={cartDataList} // <-- Pass cartDataList for normal flow
                    loadingCartItemIds={loadingCartItemIds} // Optional: for loading state
                    campaignStartDate={startDate}
                    campaignEndDate={endDate}
                    isViewingPackages={false}
                  />
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {billBoardData.map((inventoryItem: any) => {
                      // Use appropriate card based on inventory type
                      // if (inventoryItem.type === 'DEAL' || filters.category === 'deals') {
                      //   return (
                      //     <DealCard
                      //       key={inventoryItem.id}
                      //       deal={inventoryItem}
                      //       onAddToCart={() => addToCart(inventoryItem)}
                      //       onRemoveFromCart={() => deleteCartItem(inventoryItem)}
                      //       isInCart={cartDataList.some((cart: any) => 
                      //         cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                      //       )}
                      //       isLoading={loadingCartItemIds.has(inventoryItem.id)}
                      //       onClick={() => handleCardClick(inventoryItem)}
                      //     />
                      //   );
                      // }
                      // // Use PackageCard for packages
                      // else if (inventoryItem.type === 'PACKAGE' || filters.category === 'packages') {
                      //   return (
                      //     <PackageCard
                      //       key={inventoryItem.id}
                      //       package={inventoryItem}
                      //       onAddToCart={() => addToCart(inventoryItem)}
                      //       onRemoveFromCart={() => deleteCartItem(inventoryItem)}
                      //       isInCart={cartDataList.some((cart: any) => 
                      //         cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                      //       )}
                      //       isLoading={loadingCartItemIds.has(inventoryItem.id)}
                      //       onClick={() => handleCardClick(inventoryItem)}
                      //     />
                      //   );
                      // }

                      return (
                        <Card key={inventoryItem.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                          <div className="relative">
                            <div className="absolute top-3 left-3 z-10">
                              <StatusBadge inventoryItem={inventoryItem} showDot={true} />
                            </div>
                            {cartDataList.some((cart: any) =>
                              cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                            ) && (
                                <Badge className="absolute top-3 right-3 z-10 bg-emerald-600 text-white">
                                  In Cart
                                </Badge>
                              )}
                            <img
                              src={!inventoryItem.fullResImagePath || inventoryItem.fullResImagePath === "null"
                                ? `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                                : inventoryItem.fullResImagePath}
                              alt={inventoryItem.displayName || inventoryItem.name}
                              className="w-full h-40 object-cover"
                            />
                          </div>

                          <CardContent className="p-4">
                            <div className="mb-3">
                              <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1" title={inventoryItem.displayName || inventoryItem.name}>
                                {inventoryItem.displayName || inventoryItem.name}</h3>
                              <p className="text-sm text-gray-600 mb-1">{`${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`}</p>
                              {/* {inventoryItem.mediaOwnerName && (
                                <Link
                                  to={`/store/${getMediaOwner(inventoryItem.mediaOwnerName)?.id || 'ooh-unmissable'}`}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium mb-2 inline-block"
                                  onClick={() => {
                                    const currentPath = window.location.pathname;
                                    sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
                                  }}
                                >
                                  by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                </Link>
                              )} */}
                              <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                              </span>

                              {(() => {
                                let price = 0;
                                let suffix = '';

                                if (inventoryItem.type === 'DIGITAL') {
                                  price = priceFormat === 'spot-based'
                                    ? inventoryItem.perSpotPrice
                                    : inventoryItem.perDayPrice;
                                  suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                                } else {
                                  price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                    ? inventoryItem.sellingRate.monthly.month1
                                    : inventoryItem.currentPrice;
                                  suffix = '/Month';
                                }

                                const isPriceValid = !!price && price !== 0;
                                const displayCurrencyCode = inventoryItem?.price?.localCurrencies?.code || currencyCode || 'N/A';

                                const priceDisplay = isPriceValid
                                  ? `${displayCurrencyCode} ${Number(price).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}${suffix}`
                                  : 'N/A';

                                return (
                                  <>
                                    <div className="text-lg font-bold text-emerald-600">
                                      {priceDisplay}
                                    </div>

                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                      <div className="flex items-center">
                                        <Monitor className="mr-1 h-3 w-3" />
                                        <span>
                                          {inventoryItem.type === 'CLASSIC'
                                            ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                              ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                              : 'Standard')
                                            : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                              ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                              : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                                ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                                : 'Standard')}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <Users className="mr-1 h-3 w-3" />
                                        <span>{formatNumber(inventoryItem.impressionPerDay || 0)}</span>
                                      </div>
                                      <div className="flex items-center">
                                        <Clock className="mr-1 h-3 w-3" />
                                        <span>{inventoryItem.type === 'DIGITAL' ? inventoryItem?.price?.spotDuration : 'Monthly'}s</span>
                                      </div>
                                    </div>

                                    <div className="flex space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        onClick={() => handleViewDetails(inventoryItem)}
                                      >
                                        Details
                                      </Button>
                                      {!loadingCartItemIds.has(inventoryItem.id) ? (
                                        <>
                                          {cartDataList.some((cart: any) =>
                                            cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                          ) ? (
                                            <Button
                                              onClick={() => deleteCartItem(inventoryItem)}
                                              size="sm"
                                              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                            >
                                              Remove
                                            </Button>
                                          ) : (
                                            <Button
                                              onClick={() => addToCart(inventoryItem)}
                                              disabled={!isPriceValid || !inventoryItem.availableBooking || !inventoryItem.active}
                                              size="sm"
                                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                                            >
                                              {!isPriceValid ? 'Unavailable' : !inventoryItem.availableBooking || !inventoryItem.active ? 'Sold Out' : 'Add to Cart'}
                                            </Button>
                                          )}
                                        </>
                                      ) : (
                                        <Button disabled size="sm" className="flex-1">
                                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full" />
                                          Processing...
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {billBoardData.map((inventoryItem: any) => {
                      // Use DealCard for deals in list view too
                      if (filters.category === 'deals') {
                        return (
                          <DealCard
                            key={inventoryItem.id}
                            deal={inventoryItem}
                          />
                        );
                      }
                      // Use PackageCard for packages
                      else if (inventoryItem.inventoryType === "Package" || filters.category === 'packages') {
                        return (
                          <PackageCard
                            key={inventoryItem.id}
                            package={inventoryItem}
                            viewMode={viewMode}
                          />
                        );
                      }

                      return (
                        <Card key={inventoryItem.id} className="overflow-hidden hover:shadow-md transition-shadow">
                          <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                              {/* Image */}
                              <div className="relative flex-shrink-0 w-full sm:w-48">
                                <img
                                  src={!inventoryItem.fullResImagePath || inventoryItem.fullResImagePath === "null"
                                    ? `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                                    : inventoryItem.fullResImagePath}
                                  alt={inventoryItem.displayName || inventoryItem.name}
                                  className="w-full h-32 object-cover rounded-lg"
                                />
                                {cartDataList.some((cart: any) =>
                                  cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                ) && (
                                    <Badge className="absolute top-2 right-2 bg-emerald-600 text-white">
                                      In Cart
                                    </Badge>
                                  )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{inventoryItem.displayName || inventoryItem.name}</h3>
                                    <p className="text-gray-600 mb-1">{`${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`}</p>
                                    {/* {inventoryItem.mediaOwnerName && (
                                      <Link
                                        to={`/store/${getMediaOwner(inventoryItem.mediaOwnerName)?.id || 'ooh-unmissable'}`}
                                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium mb-2 inline-block"
                                        onClick={() => {
                                          // Set back URL context based on current page
                                          const currentPath = window.location.pathname;
                                          sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
                                         }}
                                      >
                                        by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                      </Link>
                                    )} */}
                                    <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                      by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                    </span>

                                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                                      <div className="flex items-center">
                                        <Monitor className="mr-1 h-4 w-4" />
                                        <span>
                                          {inventoryItem.type === 'CLASSIC'
                                            ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                              ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                              : 'Standard')
                                            : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                              ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                              : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                                ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                                : 'Standard')}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <Users className="mr-1 h-4 w-4" />
                                        <span>{formatNumber(inventoryItem.impressionPerDay || 0)} impressions</span>
                                      </div>
                                      <div className="flex items-center">
                                        <Clock className="mr-1 h-4 w-4" />
                                        <span>{inventoryItem.type === 'DIGITAL' ? inventoryItem?.price?.spotDuration : 'Monthly'}s</span>
                                      </div>
                                    </div>

                                    <StatusBadge inventoryItem={inventoryItem} showDot={true} />
                                  </div>

                                  <div className="text-right ml-4">
                                    <div className="text-2xl font-bold text-emerald-600">
                                      {(() => {
                                        let price = 0;
                                        let suffix = '';

                                        if (inventoryItem.type === 'DIGITAL') {
                                          price = priceFormat === 'spot-based'
                                            ? inventoryItem.perSpotPrice
                                            : inventoryItem.perDayPrice;
                                          suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                                        } else {
                                          price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                            ? inventoryItem.sellingRate.monthly.month1
                                            : inventoryItem.currentPrice;
                                          suffix = '/Month';
                                        }

                                        if (!price || price === 0) return 'N/A';

                                        const displayCurrencyCode = inventoryItem?.price?.localCurrencies?.code || currencyCode || 'N/A';

                                        return `${displayCurrencyCode} ${Number(price).toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })}${suffix}`;
                                      })()}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex space-x-3 mt-4 w-1/2 ml-auto">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewDetails(inventoryItem)}
                                    className="flex-1"
                                  >
                                    View Details
                                  </Button>
                                  {!loadingCartItemIds.has(inventoryItem.id) ? (
                                    <>
                                      {cartDataList.some((cart: any) =>
                                        cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                      ) ? (
                                        <Button
                                          onClick={() => deleteCartItem(inventoryItem)}
                                          size="sm"
                                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                        >
                                          Remove
                                        </Button>
                                      ) : (
                                        <Button
                                          onClick={() => addToCart(inventoryItem)}
                                          disabled={(() => {
                                            let price = 0;
                                            if (inventoryItem.type === 'DIGITAL') {
                                              price = priceFormat === 'spot-based'
                                                ? inventoryItem.perSpotPrice
                                                : inventoryItem.perDayPrice;
                                            } else {
                                              price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                                ? inventoryItem.sellingRate.monthly.month1
                                                : inventoryItem.currentPrice;
                                            }
                                            return !price || price === 0 || !inventoryItem.availableBooking || !inventoryItem.active;
                                          })()}
                                          size="sm"
                                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                          {(() => {
                                            let price = 0;
                                            if (inventoryItem.type === 'DIGITAL') {
                                              price = priceFormat === 'spot-based'
                                                ? inventoryItem.perSpotPrice
                                                : inventoryItem.perDayPrice;
                                            } else {
                                              price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                                ? inventoryItem.sellingRate.monthly.month1
                                                : inventoryItem.currentPrice;
                                            }
                                            if (!price || price === 0) return 'Unavailable';
                                            return !inventoryItem.availableBooking || !inventoryItem.active ? 'Sold Out' : 'Add to Cart';
                                          })()}
                                        </Button>
                                      )}
                                    </>
                                  ) : (
                                    <Button disabled size="sm" className="flex-1">
                                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full" />
                                      Processing...
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Pagination Controls */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t mt-6 rounded-lg">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Showing {pageStartData} - {pageEndData} of {totalDataLength} {filters.category === 'packages' ? (totalDataLength === 1 ? 'Package' : 'Packages') : (totalDataLength === 1 ? 'Inventory' : 'Inventories')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={paginationMinus}
                      disabled={currentPage === 0}
                      className="flex items-center space-x-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous</span>
                    </Button>

                    <div className="flex items-center space-x-1">
                      {getPageNumbers().map((page, index) => (
                        <div key={index}>
                          {page === '...' ? (
                            <span className="px-2 text-gray-500">...</span>
                          ) : (
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePageClick(page as number)}
                              className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                            >
                              {(page as number) + 1}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={paginationPlus}
                      disabled={currentPage >= Math.ceil(totalDataLength / dataListLength) - 1}
                      className="flex items-center space-x-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer configData={configData ?? []} base_path={base_path} yourLogo={footerLogo || yourLogo || ''} />

    </div>
  );
}